import React, { useState, useEffect, useRef, useMemo } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet, Platform, Modal, ActivityIndicator, KeyboardAvoidingView, Keyboard, useWindowDimensions } from 'react-native';
import WebScrollView from '../components/WebScrollView';
import HomePicker from '../components/HomePicker';
import { Plus, X, Clock, DollarSign, CheckCircle2, PenTool as Tool, Wrench, HardHat, Home as HomeIcon, RefreshCw, Filter, ChevronDown, ChevronUp, GripVertical, Sparkles, User, Briefcase } from '../lib/icons';
import { showAlert } from '../lib/platform';
import { supabase } from '../lib/supabase';
import { shadows } from '../lib/styles';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../lib/constants';
import { useActiveHome } from '../contexts/ActiveHomeContext';

// Native drag-and-drop list. We use `react-native-draglist` rather than
// `react-native-draggable-flatlist` because the latter depends on
// react-native-reanimated v3 internals; with our Reanimated 4 setup it
// silently fails to drag on iOS (see GitHub issues #561, #595, #572 on
// computerjazz/react-native-draggable-flatlist). `react-native-draglist`
// only depends on react-native, so it is unaffected.
let DragList: any = null;
if (Platform.OS !== 'web') {
  try {
    const mod = require('react-native-draglist');
    DragList = mod.default ?? mod;
  } catch (error: any) {
    if (__DEV__) {
      console.warn('Failed to load react-native-draglist:', error);
    }
  }
}

type EnrichmentStatus = 'processing' | 'enriched' | 'failed' | null;

interface Task {
  id: string;
  title: string;
  description: string;
  category_id: string | null;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  due_date: string | null;
  completed_at: string | null;
  home_id: string;
  category_name: string;
  skill_level: 'diy' | 'handyman' | 'professional';
  task_type: 'user_task' | 'maintenance_task';
  frequency_months: number | null;
  maintenance_task_id: string | null;
  sort_order: number;
  enrichment_status: EnrichmentStatus;
}

interface TaskEstimate {
  id: string;
  task_id: string;
  time_minutes: number;
  cost_min: number;
  cost_max: number;
  materials_needed: string[];
  tools_needed: string[];
}

interface TaskCategory {
  id: string;
  name: string;
  description: string;
  skill_level: 'diy' | 'handyman' | 'professional';
}

interface Home {
  id: string;
  address: string;
  city: string;
  state: string;
  cover_photo_url?: string;
  lot_size?: number;
  bedrooms?: number;
  bathrooms?: number;
}

/**
 * Native drag handle for a list row.
 *
 * Why a custom component instead of `<Pressable onLongPress … onPressOut …>`?
 *
 * react-native-draglist's `onDragEnd` callback calls `reset()` (which kills
 * the active drag) if `panGrantedRef` is still false at the moment we call
 * it. When the user starts moving their finger after our long-press, RN's
 * responder system transfers ownership from this handle to draglist's pan
 * responder, and dispatches `onResponderTerminate` on the old responder
 * BEFORE `onPanResponderGrant` on the new one. With a Pressable that means
 * `onPressOut` fires *before* `panGrantedRef` becomes true, so the
 * `nativeDrag.end()` we wired to it kills the drag right at the moment it
 * should have started moving.
 *
 * Handling the responder lifecycle ourselves lets us:
 *   - call `nativeDrag.end()` only on a true release (finger lift), and
 *   - explicitly NOT call it on responder termination, since the pan
 *     responder is now in charge and will fire its own release.
 */
const NativeDragHandle = ({
  nativeDrag,
  isDragging,
  onArm,
  onDisarm,
}: {
  nativeDrag?: { start: () => void; end: () => void } | null;
  isDragging: boolean;
  onArm?: () => void;
  onDisarm?: () => void;
}) => {
  const [pressed, setPressed] = React.useState(false);
  const longPressTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const activatedRef = React.useRef(false);

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  React.useEffect(() => {
    return () => cancelLongPress();
  }, []);

  return (
    <View
      // Claim the responder for any touch starting on the handle so the
      // outer ScrollView / FlatList don't steal it during the long-press
      // wait. Once draglist's pan responder is ready (after we call
      // nativeDrag.start), it will negotiate the touch away from us.
      onStartShouldSetResponder={() => !!nativeDrag}
      onResponderGrant={() => {
        setPressed(true);
        activatedRef.current = false;
        longPressTimerRef.current = setTimeout(() => {
          activatedRef.current = true;
          // "Arm" the drag *before* telling draglist about it so the parent
          // can disable the outer scroller in time for the user's first
          // finger move (otherwise the outer ScrollView wins the move and
          // the page scrolls instead of the row dragging).
          onArm?.();
          nativeDrag?.start();
        }, 150);
      }}
      onResponderRelease={() => {
        setPressed(false);
        cancelLongPress();
        // Real release (user lifted finger). If we had activated the drag
        // but never moved (so the pan responder never took over), tell
        // draglist to reset its hover state and disarm.
        if (activatedRef.current) {
          nativeDrag?.end();
          onDisarm?.();
          activatedRef.current = false;
        }
      }}
      onResponderTerminationRequest={() => true}
      onResponderTerminate={() => {
        // The pan responder has taken over (this is the *good* case — it
        // means the user is now dragging). DO NOT call nativeDrag.end():
        // that would race the pan responder's grant and abort the drag.
        // The pan responder fires its own release when the finger lifts;
        // the parent disarms then via DragList's onDragEnd prop.
        setPressed(false);
        cancelLongPress();
        activatedRef.current = false;
      }}
      style={[
        styles.dragHandle,
        (pressed || isDragging) && styles.dragHandlePressed,
      ]}
    >
      <GripVertical size={18} color={isDragging ? '#374151' : '#9ca3af'} />
    </View>
  );
};

const HoneyDoList = () => {
  const { homes: accessibleHomes, activeHome, setActiveHomeById, loading: homesLoading } = useActiveHome();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [categories, setCategories] = useState<TaskCategory[]>([]);
  const [homes, setHomes] = useState<Home[]>([]);
  const [estimates, setEstimates] = useState<Record<string, TaskEstimate>>({});
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEstimateFormOpen, setIsEstimateFormOpen] = useState(false);
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [selectedHome, setSelectedHome] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [skillFilter, setSkillFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [deleteConfirmMode, setDeleteConfirmMode] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [taskToComplete, setTaskToComplete] = useState<Task | null>(null);
  const [completionData, setCompletionData] = useState({
    completion_date: new Date().toISOString().split('T')[0],
    performed_by: 'homeowner' as 'homeowner' | 'professional',
    notes: ''
  });
  const [completing, setCompleting] = useState(false);
  
  // Quick-add task input (Google-Tasks-style). The user types a title, hits
  // Save, and we insert the task immediately with enrichment_status set to
  // 'processing'. A background call to the parse-task Edge Function fills in
  // the rest (description, category, priority, skill, estimate). Until that
  // returns, the task row shows a spinner so the user knows something is
  // happening. The bottom sheet stays open after save so they can keep
  // typing more tasks like Google Tasks does.
  const [showQuickAddSheet, setShowQuickAddSheet] = useState(false);
  const [quickAddTitle, setQuickAddTitle] = useState('');
  const [quickAddSubmitting, setQuickAddSubmitting] = useState(false);
  const [quickAddError, setQuickAddError] = useState<string | null>(null);
  const quickAddInputRef = useRef<TextInput | null>(null);
  // Track ids of tasks being enriched on the client. We seed this from the
  // optimistic local insert so the spinner shows immediately, even before
  // the next fetchTasks() roundtrip writes enrichment_status onto the row.
  const [enrichingTaskIds, setEnrichingTaskIds] = useState<Set<string>>(new Set());
  
  // Service Request Creation
  const [showServiceRequestModal, setShowServiceRequestModal] = useState(false);
  const [taskForServiceRequest, setTaskForServiceRequest] = useState<Task | null>(null);
  const [creatingServiceRequest, setCreatingServiceRequest] = useState(false);
  const [serviceRequestSuccess, setServiceRequestSuccess] = useState(false);
  
  // Dropdown picker states
  const [showStatusFilterPicker, setShowStatusFilterPicker] = useState(false);
  const [showSkillFilterPicker, setShowSkillFilterPicker] = useState(false);
  const [showTypeFilterPicker, setShowTypeFilterPicker] = useState(false);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [showPriorityPicker, setShowPriorityPicker] = useState(false);
  const [showStatusPicker, setShowStatusPicker] = useState(false);
  const [showEditCategoryPicker, setShowEditCategoryPicker] = useState(false);
  const [showEditPriorityPicker, setShowEditPriorityPicker] = useState(false);
  const [statusPickerTask, setStatusPickerTask] = useState<Task | null>(null);
  
  // Web drag-and-drop state (Pointer Events based — works on iOS / Android / desktop).
  // `draggedTaskId` is set once a drag is *active* (after long-press on touch
  // or pointerdown on mouse), not just when the user touches the handle.
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [hoveredHandleId, setHoveredHandleId] = useState<string | null>(null);

  // Native drag-and-drop coordination: we put the FlatList from
  // react-native-draglist inside the page-level WebScrollView. Without
  // intervention the outer ScrollView's pan gesture wins the user's first
  // finger move and the page scrolls instead of the row dragging. We flip
  // this flag the moment the long-press fires on the handle, so the
  // ScrollView's `scrollEnabled` toggles to false in time for the move.
  const [nativeDragArmed, setNativeDragArmed] = useState(false);

  // Sheet sizing for the bottom-sheet modals on native. We use an explicit
  // height, not flex/maxHeight, because iOS RN/Yoga can leave the modal's
  // inner ScrollView at 0 height when the parent only has inferred flex
  // constraints. Keyboard height is folded in so the sheet shrinks before
  // KeyboardAvoidingView lifts it above the keyboard.
  const { height: windowHeight } = useWindowDimensions();
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const sheetMaxHeight = Math.round(windowHeight * 0.9);
  const sheetHeight = Math.max(
    320,
    Math.min(sheetMaxHeight, windowHeight - keyboardHeight - 24),
  );

  useEffect(() => {
    if (Platform.OS !== 'ios') return;

    const showSubscription = Keyboard.addListener('keyboardWillShow', (event) => {
      setKeyboardHeight(event.endCoordinates.height);
    });
    const hideSubscription = Keyboard.addListener('keyboardWillHide', () => {
      setKeyboardHeight(0);
    });

    return () => {
      showSubscription.remove();
      hideSubscription.remove();
    };
  }, []);

  // Per-drag session state. Refs (not state) because we mutate them in
  // pointermove/up handlers and don't want to re-render on every move.
  const dragSessionRef = useRef<{
    pointerId: number;
    pointerType: 'mouse' | 'touch' | 'pen' | string;
    taskId: string;
    startIndex: number;
    startClientY: number;
    longPressTimer: number | null;
    activated: boolean;
    handleEl: HTMLElement | null;
  } | null>(null);
  const autoScrollRafRef = useRef<number | null>(null);
  const autoScrollSpeedRef = useRef<number>(0); // px / frame, signed
  const listScrollRef = useRef<
    {
      scrollTo: (o: { y?: number; x?: number; animated?: boolean }) => void;
      scrollBy?: (o: { y?: number; x?: number; animated?: boolean }) => void;
      getScrollElement?: () => HTMLElement | null;
    } | null
  >(null);
  const [justAddedTaskId, setJustAddedTaskId] = useState<string | null>(null);

  const activeTasks = useMemo(
    () => tasks.filter((t) => t.status !== 'completed'),
    [tasks]
  );
  const completedTasks = useMemo(
    () => tasks.filter((t) => t.status === 'completed'),
    [tasks]
  );
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category_id: '',
    priority: 'normal' as Task['priority'],
    due_date: '',
    home_id: ''
  });
  const [estimateForm, setEstimateForm] = useState({
    time_minutes: 60,
    cost_min: 0,
    cost_max: 0,
    materials_needed: [''],
    tools_needed: ['']
  });
  const [editTaskForm, setEditTaskForm] = useState({
    title: '',
    description: '',
    category_id: '',
    priority: 'normal' as Task['priority'],
    due_date: '',
    time_minutes: 60,
    cost_min: 0,
    cost_max: 0,
    materials_needed: [''],
    tools_needed: ['']
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    setHomes(accessibleHomes as Home[]);
    if (activeHome?.id) {
      setSelectedHome(activeHome.id);
      setFormData((prev) => ({ ...prev, home_id: activeHome.id }));
    } else if (!homesLoading) {
      setSelectedHome('');
      setLoading(false);
    }
  }, [accessibleHomes, activeHome, homesLoading]);

  useEffect(() => {
    if (selectedHome) {
      fetchTasks();
    }
  }, [selectedHome, statusFilter, skillFilter, typeFilter]);

  useEffect(() => {
    if (!justAddedTaskId) return;
    const t = setTimeout(() => setJustAddedTaskId(null), 2800);
    return () => clearTimeout(t);
  }, [justAddedTaskId]);

  // While a drag is active, prevent the page from scrolling underneath the
  // user's finger. We do this by setting `touch-action: none` on the document
  // root rather than rewriting every scroll container in the DOM. JS-driven
  // scroll (e.g. our auto-scroll while dragging near the edge) still works.
  useEffect(() => {
    if (Platform.OS !== 'web' || !draggedTaskId) return;

    const root = document.documentElement;
    const body = document.body;
    const prevRootTouchAction = root.style.touchAction;
    const prevBodyTouchAction = body.style.touchAction;
    const prevBodyOverscroll = (body.style as any).overscrollBehavior || '';
    root.style.touchAction = 'none';
    body.style.touchAction = 'none';
    (body.style as any).overscrollBehavior = 'contain';

    return () => {
      root.style.touchAction = prevRootTouchAction;
      body.style.touchAction = prevBodyTouchAction;
      (body.style as any).overscrollBehavior = prevBodyOverscroll;
    };
  }, [draggedTaskId]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('task_categories')
        .select('*')
        .order('name');

      if (error) throw error;
      setCategories(data || []);
      
      if (data && data.length > 0) {
        setFormData(prev => ({ ...prev, category_id: data[0].id }));
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchTasks = async () => {
    try {
      // Using the combined_tasks view to get both user tasks and maintenance tasks
      let query = supabase
        .from('combined_tasks')
        .select('*')
        .eq('home_id', selectedHome);

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      if (skillFilter !== 'all') {
        query = query.eq('skill_level', skillFilter);
      }

      if (typeFilter !== 'all') {
        query = query.eq('task_type', typeFilter);
      }

      const { data, error } = await query.order('sort_order', { ascending: true });

      if (error) throw error;
      setTasks(data || []);
      
      // Fetch estimates for user tasks
      const userTaskIds = data
        ?.filter(task => task.task_type === 'user_task')
        .map(task => task.id) || [];
      
      if (userTaskIds.length > 0) {
        fetchEstimates(userTaskIds);
      }

      // For maintenance tasks, create synthetic estimates
      const maintenanceEstimates: Record<string, TaskEstimate> = {};
      data?.filter(task => task.task_type === 'maintenance_task').forEach(task => {
        maintenanceEstimates[task.id] = {
          id: `maintenance-${task.id}`,
          task_id: task.id,
          time_minutes: task.frequency_months ? task.frequency_months * 30 : 60,
          cost_min: 50,
          cost_max: 150,
          materials_needed: ['Varies based on task'],
          tools_needed: ['Standard tools']
        };
      });

      setEstimates(prev => ({...prev, ...maintenanceEstimates}));
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  /** Sort order lower = higher in the list. New tasks use min(existing) - 1 so they appear first. */
  const resolveTopSortOrder = async (homeId: string): Promise<number> => {
    const { data, error } = await supabase
      .from('combined_tasks')
      .select('sort_order')
      .eq('home_id', homeId)
      .order('sort_order', { ascending: true })
      .limit(1);

    if (error) throw error;
    const min = data?.[0]?.sort_order;
    if (min === undefined || min === null) return 0;
    return min - 1;
  };

  const scrollListToTop = () => {
    requestAnimationFrame(() => {
      listScrollRef.current?.scrollTo?.({ y: 0, animated: true });
    });
  };

  const fetchEstimates = async (taskIds: string[]) => {
    try {
      const { data, error } = await supabase
        .from('task_estimates')
        .select('*')
        .in('task_id', taskIds);

      if (error) throw error;

      const estimatesMap: Record<string, TaskEstimate> = {};
      (data || []).forEach(estimate => {
        estimatesMap[estimate.task_id] = estimate;
      });

      setEstimates(prev => ({...prev, ...estimatesMap}));
    } catch (error) {
      console.error('Error fetching estimates:', error);
    }
  };

  const handleTaskReorder = async (reorderedActiveTasks: Task[]) => {
    const completed = tasks.filter((t) => t.status === 'completed');
    const merged = [...reorderedActiveTasks, ...completed];
    // Optimistically update the UI
    setTasks(merged);

    try {
      // Update sort_order for each task based on new position
      const updates = reorderedActiveTasks.map((task, index) => ({
        id: task.id,
        task_type: task.task_type,
        sort_order: index
      }));

      // Group updates by task type
      const userTaskUpdates = updates.filter(u => u.task_type === 'user_task');
      const maintenanceTaskUpdates = updates.filter(u => u.task_type === 'maintenance_task');

      // Update user tasks
      if (userTaskUpdates.length > 0) {
        for (const update of userTaskUpdates) {
          const { error } = await supabase
            .from('honeydone_tasks')
            .update({ sort_order: update.sort_order })
            .eq('id', update.id);
          
          if (error) throw error;
        }
      }

      // Update maintenance tasks
      if (maintenanceTaskUpdates.length > 0) {
        for (const update of maintenanceTaskUpdates) {
          const { error } = await supabase
            .from('maintenance_plans')
            .update({ sort_order: update.sort_order })
            .eq('id', update.id);
          
          if (error) throw error;
        }
      }
    } catch (error) {
      console.error('Error updating task order:', error);
      // Revert to original order on error
      fetchTasks();
    }
  };

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  };

  const handleAddTaskClick = () => {
    setQuickAddTitle('');
    setQuickAddError(null);
    setShowQuickAddSheet(true);
    // Focus shortly after the sheet animates in. requestAnimationFrame is
    // enough on web; on iOS the modal animation runs ~250ms so we delay
    // slightly more there to let the keyboard come up smoothly.
    const focusDelay = Platform.OS === 'web' ? 0 : 280;
    setTimeout(() => {
      quickAddInputRef.current?.focus();
    }, focusDelay);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('No authenticated user');
      }

      const sortOrder = await resolveTopSortOrder(formData.home_id);

      const { data: inserted, error } = await supabase
        .from('honeydone_tasks')
        .insert([{
          title: formData.title,
          description: formData.description,
          category_id: formData.category_id,
          priority: formData.priority,
          due_date: formData.due_date || null,
          user_id: user.id,
          home_id: formData.home_id,
          sort_order: sortOrder,
        }])
        .select('id')
        .single();

      if (error) throw error;

      if (inserted?.id) setJustAddedTaskId(inserted.id);

      setIsFormOpen(false);
      await fetchTasks();
      scrollListToTop();
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        category_id: categories.length > 0 ? categories[0].id : '',
        priority: 'normal',
        due_date: getTomorrowDate(),
        home_id: selectedHome
      });
    } catch (error: any) {
      setError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (task: Task, newStatus: Task['status']) => {
    // For maintenance tasks being marked as complete, show the completion modal
    if (task.task_type === 'maintenance_task' && newStatus === 'completed') {
      handleOpenCompleteModal(task);
      return;
    }
    
    try {
      const updates: any = { 
        status: newStatus 
      };
      
      // If completing the task, set the completed_at timestamp
      if (newStatus === 'completed') {
        updates.completed_at = new Date().toISOString();
      } else {
        // If changing from completed to another status, clear the completed_at
        updates.completed_at = null;
      }
      
      if (task.task_type === 'user_task') {
        // Update user-created task
        const { error } = await supabase
          .from('honeydone_tasks')
          .update(updates)
          .eq('id', task.id);

        if (error) throw error;
      } else {
        // Update maintenance task (for non-complete status changes)
        const { error } = await supabase
          .from('maintenance_plans')
          .update({
            ...updates,
            last_completed_date: newStatus === 'completed' ? new Date().toISOString() : null
          })
          .eq('id', task.id);

        if (error) throw error;
      }
      
      fetchTasks();
    } catch (error) {
      console.error('Error updating task status:', error);
    }
  };

  const handleOpenCompleteModal = (task: Task) => {
    setTaskToComplete(task);
    setCompletionData({
      completion_date: new Date().toISOString().split('T')[0],
      performed_by: 'homeowner',
      notes: ''
    });
    setShowCompleteModal(true);
  };

  const handleCloseCompleteModal = () => {
    setShowCompleteModal(false);
    setTaskToComplete(null);
    setCompletionData({
      completion_date: new Date().toISOString().split('T')[0],
      performed_by: 'homeowner',
      notes: ''
    });
  };

  const handleCompleteTask = async () => {
    if (!taskToComplete) return;
    
    setCompleting(true);
    setError('');

    try {
      const { error } = await supabase.rpc('complete_maintenance_task', {
        plan_id: taskToComplete.id,
        completion_date: completionData.completion_date,
        performed_by: completionData.performed_by,
        notes: completionData.notes || null,
        provider_id: null
      });

      if (error) throw error;
      
      // Refresh the tasks
      fetchTasks();
      handleCloseCompleteModal();
    } catch (error: any) {
      console.error('Error completing task:', error);
      setError('Failed to mark task as complete. Please try again.');
    } finally {
      setCompleting(false);
    }
  };

  const handleCloseQuickAddSheet = () => {
    if (quickAddSubmitting) return;
    setShowQuickAddSheet(false);
    setQuickAddTitle('');
    setQuickAddError(null);
  };

  /**
   * Resolve a fallback category id for quick-add. We use "Other" if it
   * exists, otherwise the first available category. Returned id may be
   * null only when the categories table is empty (which shouldn't happen
   * in production — the migration seeds defaults).
   */
  const resolveDefaultCategoryId = (): string | null => {
    if (!categories.length) return null;
    const other = categories.find((c) => c.name.toLowerCase() === 'other');
    return (other ?? categories[0]).id;
  };

  /**
   * Background AI enrichment for a quick-add task. Fires after the row is
   * already in the database with enrichment_status='processing'. Updates
   * the row in place when parsing succeeds; flips it to 'failed' on
   * error so the row isn't stuck spinning forever.
   */
  const enrichTaskInBackground = async (
    taskId: string,
    rawTitle: string,
    homeId: string,
  ) => {
    try {
      const homeData = homes.find((h) => h.id === homeId);
      const homeContext = homeData
        ? { address: homeData.address, city: homeData.city, state: homeData.state }
        : null;

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${SUPABASE_URL}/functions/v1/parse-task`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ taskDescription: rawTitle, homeContext }),
      });

      if (!response.ok) {
        throw new Error(`parse-task returned ${response.status}`);
      }

      const json = await response.json();
      const parsed = json?.task;
      if (!parsed) throw new Error(json?.error || 'Failed to parse task');

      // Resolve / create category. Failure here is non-fatal — we still
      // want to apply the rest of the enrichment to the task.
      let categoryId: string | null = null;
      if (parsed.category) {
        const { data: existing } = await supabase
          .from('task_categories')
          .select('id')
          .eq('name', parsed.category)
          .maybeSingle();
        if (existing) {
          categoryId = existing.id;
        } else {
          const { data: created } = await supabase
            .from('task_categories')
            .insert({ name: parsed.category, skill_level: parsed.skill_level })
            .select('id')
            .single();
          if (created) categoryId = created.id;
        }
      }

      // We let the AI overwrite the placeholder fields, but we keep the
      // user's typed text as the source of truth for the title — they
      // already see it on the row and we don't want it to flicker. The
      // model's "title" is often a tidied-up version of the same input
      // and rarely worth the visual disruption.
      const updates: Record<string, unknown> = {
        description: parsed.description ?? '',
        priority: parsed.priority ?? 'normal',
        skill_level: parsed.skill_level ?? 'diy',
        due_date: parsed.due_date ?? null,
        enrichment_status: 'enriched',
      };
      if (categoryId) updates.category_id = categoryId;

      const { error: updateError } = await supabase
        .from('honeydone_tasks')
        .update(updates)
        .eq('id', taskId);
      if (updateError) throw updateError;

      if (parsed.estimated_time_minutes || parsed.estimated_cost_min) {
        await supabase
          .from('task_estimates')
          .insert({
            task_id: taskId,
            time_minutes: parsed.estimated_time_minutes || 60,
            cost_min: parsed.estimated_cost_min || 0,
            cost_max:
              parsed.estimated_cost_max || parsed.estimated_cost_min || 0,
            materials_needed: parsed.materials_needed || [],
            tools_needed: parsed.tools_needed || [],
          });
      }
    } catch (err) {
      console.error('Background task enrichment failed:', err);
      // Mark the row as failed so the spinner stops. The task itself stays
      // in the list with the user's typed title, which is still useful.
      await supabase
        .from('honeydone_tasks')
        .update({ enrichment_status: 'failed' })
        .eq('id', taskId);
    } finally {
      setEnrichingTaskIds((prev) => {
        if (!prev.has(taskId)) return prev;
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
      // Refresh once enrichment finishes so the row picks up the new
      // category, description, estimate, etc. Only reload if we're still
      // looking at the same home — switching homes mid-flight is fine to
      // ignore since the row will be loaded next time.
      if (selectedHome === homeId) {
        fetchTasks();
      }
    }
  };

  const handleQuickAddSubmit = async () => {
    const title = quickAddTitle.trim();
    if (!title) {
      setQuickAddError('Please type a task');
      return;
    }
    if (!selectedHome) {
      setQuickAddError('Please select a home first');
      return;
    }

    setQuickAddSubmitting(true);
    setQuickAddError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const sortOrder = await resolveTopSortOrder(selectedHome);
      const defaultCategoryId = resolveDefaultCategoryId();

      const { data: inserted, error: insertError } = await supabase
        .from('honeydone_tasks')
        .insert({
          title,
          description: '',
          category_id: defaultCategoryId,
          priority: 'normal',
          skill_level: 'diy',
          status: 'pending',
          home_id: selectedHome,
          user_id: user.id,
          sort_order: sortOrder,
          enrichment_status: 'processing',
        })
        .select('id')
        .single();

      if (insertError) throw insertError;
      const taskId = inserted?.id;
      if (!taskId) throw new Error('Insert returned no id');

      setJustAddedTaskId(taskId);
      setEnrichingTaskIds((prev) => {
        const next = new Set(prev);
        next.add(taskId);
        return next;
      });

      // Reset the input so the user can keep adding tasks (Google-Tasks style).
      setQuickAddTitle('');
      // Refresh the list so the new "processing" row appears immediately,
      // then start AI enrichment in the background. We don't await
      // enrichTaskInBackground — that's the whole point.
      await fetchTasks();
      scrollListToTop();
      void enrichTaskInBackground(taskId, title, selectedHome);

      // Re-focus the input so the next task is just one keystroke away.
      requestAnimationFrame(() => {
        quickAddInputRef.current?.focus();
      });
    } catch (err: any) {
      console.error('Quick-add task failed:', err);
      setQuickAddError(err?.message || 'Failed to add task');
    } finally {
      setQuickAddSubmitting(false);
    }
  };

  const handleOpenServiceRequestModal = (task: Task) => {
    setTaskForServiceRequest(task);
    setServiceRequestSuccess(false);
    setShowServiceRequestModal(true);
  };

  const handleCloseServiceRequestModal = () => {
    setShowServiceRequestModal(false);
    setTaskForServiceRequest(null);
    setServiceRequestSuccess(false);
  };

  const handleCreateServiceRequest = async () => {
    if (!taskForServiceRequest) return;

    try {
      setCreatingServiceRequest(true);

      // Create service request with task information
      const { error } = await supabase
        .from('service_requests')
        .insert([{
          title: taskForServiceRequest.title,
          description: taskForServiceRequest.description,
          priority: taskForServiceRequest.priority,
          status: 'pending',
          home_id: taskForServiceRequest.home_id
        }]);

      if (error) throw error;

      setServiceRequestSuccess(true);
      
      // Auto-close after 2 seconds
      setTimeout(() => {
        handleCloseServiceRequestModal();
      }, 2000);
    } catch (error: any) {
      console.error('Error creating service request:', error);
      showAlert('Failed to create service request: ' + error.message);
    } finally {
      setCreatingServiceRequest(false);
    }
  };

  const addMaterialField = () => {
    setEstimateForm({
      ...estimateForm,
      materials_needed: [...estimateForm.materials_needed, '']
    });
  };

  const updateMaterialField = (index: number, value: string) => {
    const updatedMaterials = [...estimateForm.materials_needed];
    updatedMaterials[index] = value;
    setEstimateForm({
      ...estimateForm,
      materials_needed: updatedMaterials
    });
  };

  const addToolField = () => {
    setEstimateForm({
      ...estimateForm,
      tools_needed: [...estimateForm.tools_needed, '']
    });
  };

  const updateToolField = (index: number, value: string) => {
    const updatedTools = [...estimateForm.tools_needed];
    updatedTools[index] = value;
    setEstimateForm({
      ...estimateForm,
      tools_needed: updatedTools
    });
  };

  const handleEstimateSubmit = async () => {
    setSubmitting(true);
    setError('');

    try {
      if (!selectedTask) {
        throw new Error('No task selected');
      }

      const { error } = await supabase
        .from('task_estimates')
        .insert([{
          task_id: selectedTask.id,
          time_minutes: estimateForm.time_minutes,
          cost_min: estimateForm.cost_min,
          cost_max: estimateForm.cost_max,
          materials_needed: estimateForm.materials_needed.filter(m => m.trim() !== ''),
          tools_needed: estimateForm.tools_needed.filter(t => t.trim() !== '')
        }]);

      if (error) throw error;

      setIsEstimateFormOpen(false);
      setSelectedTask(null);
      
      // Reset estimate form
      setEstimateForm({
        time_minutes: 60,
        cost_min: 0,
        cost_max: 0,
        materials_needed: [''],
        tools_needed: ['']
      });
      
      fetchTasks(); // Refresh to get the new estimate
    } catch (error: any) {
      setError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTask = async () => {
    if (!editingTask || editingTask.task_type !== 'user_task') return;

    // First click: enter confirm mode
    if (!deleteConfirmMode) {
      setDeleteConfirmMode(true);
      // Auto-reset after 3 seconds if user doesn't confirm
      setTimeout(() => {
        setDeleteConfirmMode(false);
      }, 3000);
      return;
    }

    // Second click: actually delete
    setSubmitting(true);
    setError('');
    setDeleteConfirmMode(false);

    try {
      // For user tasks, delete estimates first, then the task
      const { error: estimateError } = await supabase
        .from('task_estimates')
        .delete()
        .eq('task_id', editingTask.id);

      if (estimateError) {
        console.warn('Error deleting estimates:', estimateError);
        // Continue with task deletion even if estimate deletion fails
      }

      const { error: taskError } = await supabase
        .from('honeydone_tasks')
        .delete()
        .eq('id', editingTask.id);

      if (taskError) throw taskError;

      // Close the modal and refresh tasks
      setIsTaskDetailOpen(false);
      setEditingTask(null);
      fetchTasks();
    } catch (error: any) {
      setError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveTask = async () => {
    if (!editingTask) return;

    setSubmitting(true);
    setError('');

    try {
      if (editingTask.task_type === 'user_task') {
        // Update the task
        const { error: taskError } = await supabase
          .from('honeydone_tasks')
          .update({
            title: editTaskForm.title,
            description: editTaskForm.description,
            category_id: editTaskForm.category_id,
            priority: editTaskForm.priority,
            due_date: editTaskForm.due_date || null
          })
          .eq('id', editingTask.id);

        if (taskError) throw taskError;

        // Update or create the estimate
        const existingEstimate = estimates[editingTask.id];
        const estimateData = {
          time_minutes: editTaskForm.time_minutes,
          cost_min: editTaskForm.cost_min,
          cost_max: editTaskForm.cost_max,
          materials_needed: editTaskForm.materials_needed.filter(m => m.trim() !== ''),
          tools_needed: editTaskForm.tools_needed.filter(t => t.trim() !== '')
        };

        if (existingEstimate) {
          // Update existing estimate
          const { error: estimateError } = await supabase
            .from('task_estimates')
            .update(estimateData)
            .eq('task_id', editingTask.id);

          if (estimateError) throw estimateError;
        } else {
          // Create new estimate
          const { error: estimateError } = await supabase
            .from('task_estimates')
            .insert([{
              task_id: editingTask.id,
              ...estimateData
            }]);

          if (estimateError) throw estimateError;
        }
      } else {
        // For maintenance tasks, we can only update basic info (not estimates)
        const { error: taskError } = await supabase
          .from('maintenance_plans')
          .update({
            title: editTaskForm.title,
            description: editTaskForm.description,
            category_id: editTaskForm.category_id,
            priority: editTaskForm.priority,
            due_date: editTaskForm.due_date || null
          })
          .eq('id', editingTask.id);

        if (taskError) throw taskError;
      }

      // Close the modal and refresh tasks
      setIsTaskDetailOpen(false);
      setEditingTask(null);
      fetchTasks();
    } catch (error: any) {
      setError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatTime = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 
      ? `${hours} hr ${remainingMinutes} min`
      : `${hours} hr`;
  };

  const getPriorityColor = (priority: Task['priority']) => {
    switch (priority) {
      case 'urgent': return styles.priorityUrgent;
      case 'high': return styles.priorityHigh;
      case 'normal': return styles.priorityNormal;
      case 'low': return styles.priorityLow;
      default: return styles.priorityDefault;
    }
  };

  const getStatusColor = (status: Task['status']) => {
    switch (status) {
      case 'completed': return styles.statusCompleted;
      case 'in_progress': return styles.statusInProgress;
      case 'cancelled': return styles.statusCancelled;
      default: return styles.statusPending;
    }
  };

  const getPriorityTextColor = (priority: Task['priority']) => {
    switch (priority) {
      case 'urgent': return styles.priorityUrgentText;
      case 'high': return styles.priorityHighText;
      case 'normal': return styles.priorityNormalText;
      case 'low': return styles.priorityLowText;
      default: return styles.priorityDefaultText;
    }
  };

  const getStatusTextColor = (status: Task['status']) => {
    switch (status) {
      case 'completed': return styles.statusCompletedText;
      case 'in_progress': return styles.statusInProgressText;
      case 'cancelled': return styles.statusCancelledText;
      default: return styles.statusPendingText;
    }
  };

  const getSkillLevelIcon = (skillLevel: string) => {
    switch (skillLevel) {
      case 'diy': return <Tool size={16} color="#059669" />;
      case 'handyman': return <Wrench size={16} color="#2563eb" />;
      case 'professional': return <HardHat size={16} color="#9333ea" />;
      default: return <Tool size={16} color="#6b7280" />;
    }
  };

  const getSkillLevelLabel = (skillLevel: string) => {
    switch (skillLevel) {
      case 'diy': return 'DIY';
      case 'handyman': return 'Handyman';
      case 'professional': return 'Professional';
      default: return skillLevel;
    }
  };

  const getSkillLevelColor = (skillLevel: string) => {
    switch (skillLevel) {
      case 'diy': return styles.skillDiy;
      case 'handyman': return styles.skillHandyman;
      case 'professional': return styles.skillProfessional;
      default: return styles.skillDefault;
    }
  };

  const getTaskTypeLabel = (taskType: string) => {
    return taskType === 'user_task' ? 'Custom Task' : 'Maintenance Task';
  }

  const getTaskTypeColor = (taskType: string) => {
    return taskType === 'user_task' 
      ? styles.taskTypeUser
      : styles.taskTypeMaintenance;
  }

  const toggleTaskExpanded = (taskId: string) => {
    setExpandedTasks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const openTaskDetail = (task: Task) => {
    setEditingTask(task);
    setDeleteConfirmMode(false); // Reset delete confirm mode
    const estimate = estimates[task.id];
    
    setEditTaskForm({
      title: task.title,
      description: task.description,
      category_id: task.category_id || '',
      priority: task.priority,
      due_date: task.due_date || '',
      time_minutes: estimate?.time_minutes || 60,
      cost_min: estimate?.cost_min || 0,
      cost_max: estimate?.cost_max || 0,
      materials_needed: estimate?.materials_needed?.length ? estimate.materials_needed : [''],
      tools_needed: estimate?.tools_needed?.length ? estimate.tools_needed : ['']
    });
    
    setIsTaskDetailOpen(true);
  };

  // ---- Drag-to-reorder (web) -----------------------------------------------
  //
  // The list is reorderable on web via a dedicated drag handle on each row.
  // We use Pointer Events (not HTML5 drag-and-drop, which is effectively
  // unsupported on iOS Safari, and not raw TouchEvents, whose default
  // behavior is hard to suppress reliably from React's synthetic listeners).
  //
  // UX:
  //  - Touch / pen: long-press (~220ms) the handle to "lift" the row, then
  //    move your finger anywhere to reorder. Releasing drops it. Moving
  //    before the long-press fires cancels and lets the page scroll
  //    normally — so plain swipes through the list still scroll.
  //  - Mouse: pressing the handle activates immediately and drags follow the
  //    cursor, matching native desktop expectations.
  //
  // While a drag is active we auto-scroll the list when the pointer is near
  // the top/bottom edge of the visible scroll viewport.

  const LONG_PRESS_MS = 220;
  const LONG_PRESS_TOLERANCE_PX = 8;
  const AUTO_SCROLL_EDGE_PX = 64;
  const AUTO_SCROLL_MAX_SPEED_PX = 14;

  const cancelAutoScroll = () => {
    if (autoScrollRafRef.current !== null) {
      cancelAnimationFrame(autoScrollRafRef.current);
      autoScrollRafRef.current = null;
    }
    autoScrollSpeedRef.current = 0;
  };

  const ensureAutoScrollLoop = () => {
    if (autoScrollRafRef.current !== null) return;
    const tick = () => {
      const speed = autoScrollSpeedRef.current;
      if (speed === 0) {
        autoScrollRafRef.current = null;
        return;
      }
      const scroller = listScrollRef.current;
      if (scroller?.scrollBy) {
        scroller.scrollBy({ y: speed });
      }
      autoScrollRafRef.current = requestAnimationFrame(tick);
    };
    autoScrollRafRef.current = requestAnimationFrame(tick);
  };

  const updateAutoScrollFromPointer = (clientY: number) => {
    const scroller = listScrollRef.current?.getScrollElement?.();
    if (!scroller) {
      autoScrollSpeedRef.current = 0;
      return;
    }
    const rect = scroller.getBoundingClientRect();
    const distFromTop = clientY - rect.top;
    const distFromBottom = rect.bottom - clientY;
    let speed = 0;
    if (distFromTop < AUTO_SCROLL_EDGE_PX) {
      const t = Math.max(0, AUTO_SCROLL_EDGE_PX - distFromTop) / AUTO_SCROLL_EDGE_PX;
      speed = -Math.ceil(t * AUTO_SCROLL_MAX_SPEED_PX);
    } else if (distFromBottom < AUTO_SCROLL_EDGE_PX) {
      const t = Math.max(0, AUTO_SCROLL_EDGE_PX - distFromBottom) / AUTO_SCROLL_EDGE_PX;
      speed = Math.ceil(t * AUTO_SCROLL_MAX_SPEED_PX);
    }
    autoScrollSpeedRef.current = speed;
    if (speed !== 0) {
      ensureAutoScrollLoop();
    }
  };

  /** Compute the index in `activeTasks` where the dragged item should land
   *  given a pointer Y position, by inspecting the rendered task elements. */
  const computeDropIndex = (clientY: number): number => {
    if (Platform.OS !== 'web') return 0;
    const els = Array.from(
      document.querySelectorAll('[data-active-task-id]')
    ) as HTMLElement[];
    if (els.length === 0) return 0;
    for (let i = 0; i < els.length; i++) {
      const rect = els[i].getBoundingClientRect();
      const center = rect.top + rect.height / 2;
      if (clientY < center) return i;
    }
    return els.length - 1;
  };

  const activateDrag = () => {
    if (Platform.OS !== 'web') return;
    const session = dragSessionRef.current;
    if (!session || session.activated) return;
    session.activated = true;
    if (session.longPressTimer !== null) {
      window.clearTimeout(session.longPressTimer);
      session.longPressTimer = null;
    }
    setDraggedTaskId(session.taskId);
    setDragOverIndex(session.startIndex);
    if (session.pointerType !== 'mouse' && typeof navigator !== 'undefined') {
      try {
        (navigator as any).vibrate?.(8);
      } catch {
        /* ignore — vibrate not supported */
      }
    }
  };

  const endDragSession = (commit: boolean) => {
    const session = dragSessionRef.current;
    cancelAutoScroll();
    if (!session) {
      setDraggedTaskId(null);
      setDragOverIndex(null);
      return;
    }
    if (session.longPressTimer !== null) {
      window.clearTimeout(session.longPressTimer);
      session.longPressTimer = null;
    }
    if (session.handleEl && session.handleEl.hasPointerCapture?.(session.pointerId)) {
      try {
        session.handleEl.releasePointerCapture(session.pointerId);
      } catch {
        /* ignore */
      }
    }
    if (commit && session.activated) {
      const targetIndex = dragOverIndex;
      const fromIndex = session.startIndex;
      if (
        targetIndex !== null &&
        targetIndex !== fromIndex &&
        fromIndex >= 0 &&
        fromIndex < activeTasks.length
      ) {
        const newActive = [...activeTasks];
        const [removed] = newActive.splice(fromIndex, 1);
        const insertAt = Math.max(0, Math.min(targetIndex, newActive.length));
        newActive.splice(insertAt, 0, removed);
        handleTaskReorder(newActive);
      }
    }
    dragSessionRef.current = null;
    setDraggedTaskId(null);
    setDragOverIndex(null);
  };

  const handleHandlePointerDown = (
    e: React.PointerEvent<HTMLDivElement>,
    taskId: string,
    index: number,
  ) => {
    // Only respond to the primary button for mouse / pen.
    if (e.button !== undefined && e.button !== 0) return;
    e.stopPropagation();
    const handleEl = e.currentTarget;
    try {
      handleEl.setPointerCapture(e.pointerId);
    } catch {
      /* ignore — capture is best-effort */
    }
    const session = {
      pointerId: e.pointerId,
      pointerType: e.pointerType || 'mouse',
      taskId,
      startIndex: index,
      startClientY: e.clientY,
      longPressTimer: null as number | null,
      activated: false,
      handleEl,
    };
    dragSessionRef.current = session;

    if (session.pointerType === 'mouse') {
      // Mouse / desktop: activate immediately so click+drag works as expected.
      activateDrag();
    } else {
      // Touch / pen: wait for a long-press before activating so plain
      // vertical swipes still scroll the list.
      session.longPressTimer = window.setTimeout(activateDrag, LONG_PRESS_MS);
    }
  };

  const handleHandlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const session = dragSessionRef.current;
    if (!session || session.pointerId !== e.pointerId) return;

    if (!session.activated) {
      // Long-press not yet fired. If the user moves more than the tolerance
      // (typically because they're trying to scroll), cancel the press and
      // release the pointer so the browser can take over scrolling.
      if (Math.abs(e.clientY - session.startClientY) > LONG_PRESS_TOLERANCE_PX) {
        if (session.longPressTimer !== null) {
          window.clearTimeout(session.longPressTimer);
          session.longPressTimer = null;
        }
        if (session.handleEl?.hasPointerCapture?.(session.pointerId)) {
          try {
            session.handleEl.releasePointerCapture(session.pointerId);
          } catch {
            /* ignore */
          }
        }
        dragSessionRef.current = null;
      }
      return;
    }

    // Active drag: update insertion index + auto-scroll.
    e.preventDefault();
    const next = computeDropIndex(e.clientY);
    if (next !== dragOverIndex) {
      setDragOverIndex(next);
    }
    updateAutoScrollFromPointer(e.clientY);
  };

  const handleHandlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const session = dragSessionRef.current;
    if (!session || session.pointerId !== e.pointerId) return;
    endDragSession(true);
  };

  const handleHandlePointerCancel = (e: React.PointerEvent<HTMLDivElement>) => {
    const session = dragSessionRef.current;
    if (!session || session.pointerId !== e.pointerId) return;
    endDragSession(false);
  };

  // Make sure we never leak the auto-scroll loop or a stale pointer capture.
  useEffect(() => {
    return () => {
      cancelAutoScroll();
      const session = dragSessionRef.current;
      if (session?.longPressTimer !== null && session?.longPressTimer !== undefined) {
        window.clearTimeout(session.longPressTimer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render task item (used by both web and native).
  //
  // On native, `nativeDrag` is the pair of callbacks supplied by
  // react-native-draglist's renderItem. `start` is invoked from the drag
  // handle's onLongPress (so a brief tap doesn't begin a drag), and `end`
  // is invoked from onPressOut so the list can reset its hover state if the
  // user releases without actually moving.
  const renderTaskItem = (
    task: Task,
    index: number,
    isDragging: boolean = false,
    allowReorder: boolean = true,
    nativeDrag?: { start: () => void; end: () => void } | null,
  ) => {
    const estimate = estimates[task.id];
    const isExpanded = expandedTasks.has(task.id);
    const isWebDragging = Platform.OS === 'web' && draggedTaskId === task.id;
    const isWebDragOver = Platform.OS === 'web' && dragOverIndex === index && draggedTaskId !== task.id;
    // Show the spinner if either the row's persisted enrichment_status is
    // 'processing', or if we just kicked off enrichment locally and the
    // refetch hasn't landed yet. The local set is the canonical source of
    // truth on the very first render after an optimistic insert.
    const isEnriching =
      task.task_type === 'user_task' &&
      (enrichingTaskIds.has(task.id) || task.enrichment_status === 'processing');
    
    const cardStyle = [
      styles.taskCard, 
      isExpanded && styles.taskCardExpanded, 
      (isDragging || isWebDragging) && styles.taskCardDragging,
      isWebDragOver && styles.taskCardDragOver,
      justAddedTaskId === task.id && styles.taskCardJustAdded,
    ];
    
    const content = (
      <View style={styles.taskCardContent}>
          {/* Drag Handle - Upper Left */}
          {allowReorder && (
          <View style={styles.dragHandleContainer}>
            {Platform.OS === 'web' ? (
              <div
                role="button"
                aria-label="Drag to reorder"
                onMouseEnter={() => setHoveredHandleId(task.id)}
                onMouseLeave={() => setHoveredHandleId(null)}
                onPointerDown={(e: React.PointerEvent<HTMLDivElement>) =>
                  handleHandlePointerDown(e, task.id, index)
                }
                onPointerMove={handleHandlePointerMove}
                onPointerUp={handleHandlePointerUp}
                onPointerCancel={handleHandlePointerCancel}
                onContextMenu={(e: React.MouseEvent) => e.preventDefault()}
                style={{
                  ...StyleSheet.flatten(styles.dragHandle),
                  ...(hoveredHandleId === task.id ? StyleSheet.flatten(styles.dragHandleHovered) : {}),
                  ...(isWebDragging ? StyleSheet.flatten(styles.dragHandlePressed) : {}),
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  // touchAction:none on the handle so a touch starting here
                  // never triggers a browser pan/scroll. The rest of the
                  // row keeps default touch-action so plain swipes through
                  // the list still scroll the page naturally.
                  touchAction: 'none',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  WebkitTouchCallout: 'none',
                  cursor: isWebDragging ? 'grabbing' : 'grab',
                } as any}
              >
                <GripVertical size={18} color={isWebDragging ? '#374151' : '#9ca3af'} />
              </div>
            ) : (
              <NativeDragHandle
                nativeDrag={nativeDrag ?? null}
                isDragging={isDragging}
                onArm={() => setNativeDragArmed(true)}
                onDisarm={() => setNativeDragArmed(false)}
              />
            )}
          </View>
          )}
          
          {/* Collapsed View - Always Visible — whole row (title + chevron) toggles expand */}
          <Pressable
            onPress={() => toggleTaskExpanded(task.id)}
            style={({ pressed }) => [
              styles.taskHeader,
              pressed && styles.taskHeaderPressed,
            ]}
          >
            <View style={styles.taskHeaderContent}>
              <Text style={styles.taskTitle}>{task.title}</Text>
              {isEnriching && (
                <View style={styles.enrichingIndicator}>
                  <ActivityIndicator size="small" color="#6366f1" />
                  <Text style={styles.enrichingText}>
                    Adding details with AI…
                  </Text>
                </View>
              )}
              {!isEnriching && task.due_date && (
                <Text style={styles.dueDateText}>
                  Due: {new Date(task.due_date).toLocaleDateString()}
                </Text>
              )}
            </View>
            <View style={styles.chevronContainer}>
              {isExpanded ? (
                <ChevronUp size={20} color="#6b7280" />
              ) : (
                <ChevronDown size={20} color="#6b7280" />
              )}
            </View>
          </Pressable>
          
          {/* Compact Action Bar */}
          {!isExpanded && (
            <View style={styles.compactActionBar}>
              <Pressable
                onPress={(e) => {
                  e.stopPropagation();
                  handleOpenServiceRequestModal(task);
                }}
                style={styles.compactActionButton}
              >
                <Text style={styles.compactActionButtonText}>Request Service</Text>
              </Pressable>
              <Pressable
                onPress={(e) => {
                  e.stopPropagation();
                  if (task.status !== 'completed') {
                    handleStatusChange(task, 'completed');
                  }
                }}
                style={[
                  styles.compactActionButton,
                  styles.compactActionButtonPrimary,
                  task.status === 'completed' && styles.compactActionButtonDisabled
                ]}
                disabled={task.status === 'completed'}
              >
                <CheckCircle2 size={16} color={task.status === 'completed' ? "#9ca3af" : "#ffffff"} />
                <Text style={[
                  styles.compactActionButtonText,
                  styles.compactActionButtonTextPrimary,
                  task.status === 'completed' && styles.compactActionButtonTextDisabled
                ]}>
                  {task.status === 'completed' ? 'Done' : 'Mark it Done'}
                </Text>
              </Pressable>
            </View>
          )}

          {/* Expanded View */}
          {isExpanded && (
            <View style={styles.taskExpandedContent}>
              <Text style={styles.taskDescription}>{task.description}</Text>
              
              <View style={styles.taskMeta}>
                <View style={[styles.metaBadge, getTaskTypeColor(task.task_type)]}>
                  {task.task_type === 'maintenance_task' ? 
                    <RefreshCw size={12} color="#92400e" /> : 
                    <CheckCircle2 size={12} color="#4f46e5" />
                  }
                  <Text style={styles.metaBadgeText}>{getTaskTypeLabel(task.task_type)}</Text>
                  {task.frequency_months && (
                    <Text style={styles.metaBadgeText}>
                      {' '}(Every {task.frequency_months} {task.frequency_months === 1 ? 'month' : 'months'})
                    </Text>
                  )}
                </View>
                
                <View style={[styles.metaBadge, getSkillLevelColor(task.skill_level)]}>
                  {getSkillLevelIcon(task.skill_level)}
                  <Text style={styles.metaBadgeText}>{getSkillLevelLabel(task.skill_level)}</Text>
                </View>
                
                <View style={styles.metaBadge}>
                  <HomeIcon size={12} color="#6b7280" />
                  <Text style={styles.metaBadgeText}>{task.category_name}</Text>
                </View>
                
                {estimate && (
                  <>
                    <View style={styles.metaBadge}>
                      <Clock size={12} color="#6b7280" />
                      <Text style={styles.metaBadgeText}>{formatTime(estimate.time_minutes)}</Text>
                    </View>
                    <View style={styles.metaBadge}>
                      <DollarSign size={12} color="#6b7280" />
                      <Text style={styles.metaBadgeText}>
                        {formatCurrency(estimate.cost_min)} - {formatCurrency(estimate.cost_max)}
                      </Text>
                    </View>
                  </>
                )}
              </View>
              
              {estimate && (estimate.materials_needed.length > 0 || estimate.tools_needed.length > 0) && (
                <View style={styles.estimateDetails}>
                  {estimate.materials_needed.length > 0 && (
                    <View style={styles.estimateSection}>
                      <Text style={styles.estimateSectionTitle}>Materials Needed:</Text>
                      {estimate.materials_needed.map((material, index) => (
                        <Text key={index} style={styles.estimateItem}>• {material}</Text>
                      ))}
                    </View>
                  )}
                  
                  {estimate.tools_needed.length > 0 && (
                    <View style={styles.estimateSection}>
                      <Text style={styles.estimateSectionTitle}>Tools Needed:</Text>
                      {estimate.tools_needed.map((tool, index) => (
                        <Text key={index} style={styles.estimateItem}>• {tool}</Text>
                      ))}
                    </View>
                  )}
                </View>
              )}

              <View style={styles.taskActions}>
                <View style={styles.statusSelector}>
                  <Text style={styles.statusLabel}>Status:</Text>
                  <Pressable
                    onPress={(e) => {
                      e.stopPropagation();
                      setStatusPickerTask(task);
                      setShowStatusPicker(true);
                    }}
                    style={styles.statusPickerButton}
                  >
                    <Text style={styles.statusPickerText}>
                      {task.status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    </Text>
                    <ChevronDown size={14} color="#4b5563" />
                  </Pressable>
                </View>
                
                <View style={styles.actionButtons}>
                  {!estimate && task.task_type === 'user_task' && (
                    <Pressable
                      onPress={(e) => {
                        e.stopPropagation();
                        setSelectedTask(task);
                        setIsEstimateFormOpen(true);
                      }}
                      style={styles.secondaryButton}
                    >
                      <Text style={styles.secondaryButtonText}>Add Estimate</Text>
                    </Pressable>
                  )}
                  <Pressable
                    onPress={(e) => {
                      e.stopPropagation();
                      handleOpenServiceRequestModal(task);
                    }}
                    style={styles.serviceRequestButton}
                  >
                    <Text style={styles.serviceRequestButtonText}>Request Service</Text>
                  </Pressable>
                  <Pressable
                    onPress={(e) => {
                      e.stopPropagation();
                      openTaskDetail(task);
                    }}
                    style={styles.primaryButton}
                  >
                    <Text style={styles.primaryButtonText}>Edit Task</Text>
                  </Pressable>
                </View>
              </View>
            </View>
          )}
      </View>
    );
    
    if (Platform.OS === 'web') {
      // Web: render each task as a plain div so default touch behavior
      // (i.e. scrolling) is preserved. The reorder gesture is initiated
      // exclusively from the dedicated drag handle, which uses Pointer
      // Events with a long-press on touch devices.
      const flatStyle = StyleSheet.flatten(cardStyle);
      if (!allowReorder) {
        return (
          <div key={task.id} style={flatStyle as any}>
            {content}
          </div>
        );
      }
      return (
        <div
          key={task.id}
          data-active-task-id={task.id}
          style={{
            ...flatStyle,
            // Smoothly slide rows out of the way of the active drop slot.
            transition: draggedTaskId
              ? 'transform 180ms ease, background-color 180ms ease'
              : 'background-color 180ms ease',
          } as any}
        >
          {content}
        </div>
      );
    }
    
    // Native: Use View
    return (
      <View key={task.id} style={cardStyle}>
        {content}
      </View>
    );
  };

  // Custom Dropdown Picker Component
  const DropdownPicker = ({ 
    visible, 
    onClose, 
    options, 
    selectedValue, 
    onSelect 
  }: { 
    visible: boolean; 
    onClose: () => void; 
    options: Array<{ value: string; label: string }>; 
    selectedValue: string; 
    onSelect: (value: string) => void;
  }) => {
    return (
      <Modal
        visible={visible}
        transparent
        animationType="slide"
        onRequestClose={onClose}
      >
        <View style={styles.pickerModalOverlay}>
          <Pressable style={styles.pickerModalBackdrop} onPress={onClose} />
          <View style={styles.pickerModalContent}>
            <View style={styles.pickerHeader}>
              <Text style={styles.pickerHeaderText}>Select Option</Text>
              <Pressable onPress={onClose}>
                <X size={20} color="#6b7280" />
              </Pressable>
            </View>
            <WebScrollView style={styles.pickerOptions}>
              {options.map((option) => (
                <Pressable
                  key={option.value}
                  onPress={() => {
                    onSelect(option.value);
                    onClose();
                  }}
                  style={[
                    styles.pickerOption,
                    selectedValue === option.value && styles.pickerOptionSelected
                  ]}
                >
                  <Text style={[
                    styles.pickerOptionText,
                    selectedValue === option.value && styles.pickerOptionTextSelected
                  ]}>
                    {option.label}
                  </Text>
                </Pressable>
              ))}
            </WebScrollView>
          </View>
        </View>
      </Modal>
    );
  };

  const selectedCategory = categories.find(c => c.id === formData.category_id);
  const selectedEditCategory = categories.find(c => c.id === editTaskForm.category_id);

  return (
    <View style={styles.container}>
      <WebScrollView
        ref={listScrollRef}
        style={styles.scrollView}
        contentContainerStyle={styles.contentContainer}
        // Disable the outer page scroll while a native drag is armed so the
        // ScrollView doesn't intercept the user's first move and steal the
        // touch from react-native-draglist's pan responder.
        scrollEnabled={!nativeDragArmed}
      >
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <Text style={styles.title}>HoneyDone List</Text>
            <Text style={styles.subtitle}>Manage your home projects and maintenance tasks</Text>
          </View>
        </View>

      <View style={styles.card}>
        <View style={styles.cardContent}>
          {homes.length > 1 && (
            <View style={styles.homePickerSection}>
              <HomePicker label="Home" compact />
            </View>
          )}

          {/* Filters Section - Hidden for now, uncomment to re-enable */}
          {false && (
            <View style={styles.filtersSection}>
              {/* Filter Toggle */}
              <View style={styles.filterRow}>
                <Pressable
                  onPress={() => setShowFilters(!showFilters)}
                  style={styles.filterToggleButton}
                >
                  <Filter size={16} color="#4b5563" />
                  <Text style={styles.filterToggleText}>Filters</Text>
                </Pressable>
                {(statusFilter !== 'all' || skillFilter !== 'all' || typeFilter !== 'all') && (
                  <View style={styles.activeBadge}>
                    <Text style={styles.activeBadgeText}>Active</Text>
                  </View>
                )}
                <Pressable
                  onPress={fetchTasks}
                  style={styles.refreshButton}
                >
                  <RefreshCw size={20} color="#4b5563" />
                </Pressable>
              </View>

              {/* Collapsible Filters */}
              {showFilters && (
                <View style={styles.filtersContainer}>
                  <Pressable
                    onPress={() => setShowStatusFilterPicker(true)}
                    style={styles.filterPickerButton}
                  >
                    <Text style={styles.filterPickerText}>
                      {statusFilter === 'all' ? 'All Statuses' : statusFilter.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    </Text>
                    <ChevronDown size={16} color="#4b5563" />
                  </Pressable>
                  <Pressable
                    onPress={() => setShowSkillFilterPicker(true)}
                    style={styles.filterPickerButton}
                  >
                    <Text style={styles.filterPickerText}>
                      {skillFilter === 'all' ? 'All Skill Levels' : skillFilter.charAt(0).toUpperCase() + skillFilter.slice(1)}
                    </Text>
                    <ChevronDown size={16} color="#4b5563" />
                  </Pressable>
                  <Pressable
                    onPress={() => setShowTypeFilterPicker(true)}
                    style={styles.filterPickerButton}
                  >
                    <Text style={styles.filterPickerText}>
                      {typeFilter === 'all' ? 'All Task Types' : typeFilter === 'user_task' ? 'Custom Tasks' : 'Maintenance Tasks'}
                    </Text>
                    <ChevronDown size={16} color="#4b5563" />
                  </Pressable>
                </View>
              )}
            </View>
          )}

          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#9ca3af" />
            </View>
          ) : activeTasks.length === 0 && completedTasks.length === 0 ? (
            <View style={styles.emptyContainer}>
              <CheckCircle2 size={48} color="#9ca3af" />
              <Text style={styles.emptyTitle}>No Tasks Found</Text>
              <Text style={styles.emptyText}>
                {statusFilter !== 'all' || skillFilter !== 'all' || typeFilter !== 'all'
                  ? "No tasks match your filter criteria"
                  : homes.length > 1
                  ? "No tasks for this home. Try switching homes using the picker above."
                  : "Start adding tasks to your HoneyDone list"}
              </Text>
              <Pressable
                onPress={handleAddTaskClick}
                style={styles.emptyButton}
              >
                <Plus size={16} color="#ffffff" />
                <Text style={styles.emptyButtonText}>Add Your First Task</Text>
              </Pressable>
            </View>
          ) : (
            <View>
              {activeTasks.length > 0 ? (
                Platform.OS === 'web' ? (
                  // Web: Pointer-Events-based drag-to-reorder. The list is
                  // a plain div so default scrolling is preserved everywhere
                  // except the drag handle on each row.
                  <div style={StyleSheet.flatten(styles.tasksList) as any}>
                    {activeTasks.map((task, index) => renderTaskItem(task, index))}
                  </div>
                ) : DragList ? (
                  // Native: react-native-draglist. Two coordination notes:
                  //
                  // 1. scrollEnabled={false}. The page-level WebScrollView
                  //    is the page's scroller. Without this prop, the
                  //    inner FlatList's pan gesture claims vertical swipes
                  //    inside its bounds — but it has nothing to scroll
                  //    (its content already fits in the height the outer
                  //    ScrollView gives it), so it just *blocks* the
                  //    outer ScrollView from scrolling. Disabling the
                  //    inner scroll lets touches propagate up. We lose
                  //    auto-scroll-while-dragging past the visible area,
                  //    which is an acceptable trade-off for now.
                  //
                  // 2. NativeDragHandle long-presses arm a drag and
                  //    setNativeDragArmed(true) disables outer scroll
                  //    before the user's first move so draglist's pan
                  //    responder wins. onDragEnd here fires on the real
                  //    drop after a pan, re-enabling scroll.
                  <DragList
                    data={activeTasks}
                    keyExtractor={(item: Task) => item.id}
                    scrollEnabled={false}
                    onDragBegin={() => setNativeDragArmed(true)}
                    onDragEnd={() => setNativeDragArmed(false)}
                    onReordered={(fromIndex: number, toIndex: number) => {
                      if (fromIndex === toIndex) return;
                      const newActive = [...activeTasks];
                      const [removed] = newActive.splice(fromIndex, 1);
                      const insertAt = Math.max(
                        0,
                        Math.min(toIndex, newActive.length),
                      );
                      newActive.splice(insertAt, 0, removed);
                      handleTaskReorder(newActive);
                    }}
                    renderItem={({
                      item: task,
                      index: idx,
                      isActive,
                      onDragStart,
                      onDragEnd,
                    }: any) =>
                      renderTaskItem(task, idx, isActive, true, {
                        start: onDragStart,
                        end: onDragEnd,
                      })
                    }
                    contentContainerStyle={styles.tasksList}
                  />
                ) : (
                  // Fallback: Regular list if draggable components aren't available
                  <View style={styles.tasksList}>
                    {activeTasks.map((task, index) => renderTaskItem(task, index))}
                  </View>
                )
              ) : (
                <View style={styles.noActiveTasksHint}>
                  <Text style={styles.noActiveTasksHintText}>No open tasks</Text>
                </View>
              )}

              {completedTasks.length > 0 && (
                <View
                  style={[
                    styles.completedSection,
                    activeTasks.length > 0 && styles.completedSectionAfterActive,
                  ]}
                >
                  <Text style={styles.completedSectionHeading}>Completed</Text>
                  <View style={styles.completedTasksList}>
                    {completedTasks.map((task) => renderTaskItem(task, 0, false, false))}
                  </View>
                </View>
              )}
            </View>
          )}
        </View>
      </View>

      {/* Add Task Modal */}
      <Modal
        visible={isFormOpen}
        transparent
        animationType="slide"
        onRequestClose={() => setIsFormOpen(false)}
      >
        <KeyboardAvoidingView
          style={styles.modalOverlay}
          // iOS: 'padding' adds bottom padding equal to the keyboard's
          // height to this container. Combined with the explicit
          // keyboard-aware sheetHeight below, the sheet lifts and shrinks
          // so the focused input stays visible.
          // Android handles keyboard avoidance natively with windowSoftInput.
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          <Pressable style={styles.modalBackdrop} onPress={() => setIsFormOpen(false)} />
          <View
            style={[
              styles.modalContent,
              Platform.OS !== 'web' && { height: sheetHeight },
            ]}
          >
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Add New Task</Text>
              <Pressable onPress={() => setIsFormOpen(false)}>
                <X size={24} color="#9ca3af" />
              </Pressable>
            </View>

            <WebScrollView
              style={styles.modalBody}
              contentContainerStyle={styles.modalBodyContent}
              // Let taps on buttons (e.g. Cancel) work even while a text
              // input has focus and the keyboard is up.
              keyboardShouldPersistTaps="handled"
            >
              <View style={styles.formGroup}>
                <Text style={styles.label}>Task Title *</Text>
                <TextInput
                  style={styles.input}
                  value={formData.title}
                  onChangeText={(value) => setFormData({ ...formData, title: value })}
                  placeholder="Enter task title"
                  placeholderTextColor="#9ca3af"
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.label}>Description</Text>
                <TextInput
                  style={[styles.input, styles.textArea]}
                  value={formData.description}
                  onChangeText={(value) => setFormData({ ...formData, description: value })}
                  placeholder="Describe the task..."
                  placeholderTextColor="#9ca3af"
                  multiline
                  numberOfLines={3}
                />
              </View>

              <View style={styles.formRow}>
                <View style={styles.formGroup}>
                  <Text style={styles.label}>Category</Text>
                  <Pressable
                    onPress={() => setShowCategoryPicker(true)}
                    style={styles.pickerButton}
                  >
                    <Text style={styles.pickerButtonText}>
                      {selectedCategory?.name || 'Select Category'}
                    </Text>
                    <ChevronDown size={20} color="#4b5563" />
                  </Pressable>
                </View>

                <View style={styles.formGroup}>
                  <Text style={styles.label}>Priority</Text>
                  <Pressable
                    onPress={() => setShowPriorityPicker(true)}
                    style={styles.pickerButton}
                  >
                    <Text style={styles.pickerButtonText}>
                      {formData.priority.charAt(0).toUpperCase() + formData.priority.slice(1)}
                    </Text>
                    <ChevronDown size={20} color="#4b5563" />
                  </Pressable>
                </View>
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.label}>Due Date</Text>
                <TextInput
                  style={styles.input}
                  value={formData.due_date}
                  onChangeText={(value) => setFormData({ ...formData, due_date: value })}
                  placeholder="YYYY-MM-DD"
                  placeholderTextColor="#9ca3af"
                />
              </View>

              {error && (
                <Text style={styles.errorText}>{error}</Text>
              )}
            </WebScrollView>

            <View style={styles.modalFooter}>
              <Pressable
                onPress={() => setIsFormOpen(false)}
                style={styles.cancelButton}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </Pressable>
              <Pressable
                onPress={handleSubmit}
                disabled={submitting || !formData.title.trim()}
                style={[styles.submitButton, (submitting || !formData.title.trim()) && styles.submitButtonDisabled]}
              >
                {submitting ? (
                  <ActivityIndicator size="small" color="#ffffff" />
                ) : (
                  <Text style={styles.submitButtonText}>Add Task</Text>
                )}
              </Pressable>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* Task Detail Modal */}
      <Modal
        visible={isTaskDetailOpen}
        transparent
        animationType="slide"
        onRequestClose={() => {
          setIsTaskDetailOpen(false);
          setDeleteConfirmMode(false);
        }}
      >
        <KeyboardAvoidingView
          style={styles.modalOverlay}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          <Pressable style={styles.modalBackdrop} onPress={() => {
            setIsTaskDetailOpen(false);
            setDeleteConfirmMode(false);
          }} />
          <View
            style={[
              styles.modalContent,
              Platform.OS !== 'web' && { height: sheetHeight },
            ]}
          >
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Edit Task</Text>
              <Pressable onPress={() => {
                setIsTaskDetailOpen(false);
                setDeleteConfirmMode(false);
              }}>
                <X size={24} color="#9ca3af" />
              </Pressable>
            </View>

            <WebScrollView
              style={styles.modalBody}
              contentContainerStyle={styles.modalBodyContent}
              keyboardShouldPersistTaps="handled"
            >
              <View style={styles.formGroup}>
                <Text style={styles.label}>Task Title</Text>
                <TextInput
                  style={styles.input}
                  value={editTaskForm.title}
                  onChangeText={(value) => setEditTaskForm({ ...editTaskForm, title: value })}
                  placeholder="Enter task title"
                  placeholderTextColor="#9ca3af"
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.label}>Description</Text>
                <TextInput
                  style={[styles.input, styles.textArea]}
                  value={editTaskForm.description}
                  onChangeText={(value) => setEditTaskForm({ ...editTaskForm, description: value })}
                  placeholder="Describe the task..."
                  placeholderTextColor="#9ca3af"
                  multiline
                  numberOfLines={3}
                />
              </View>

              <View style={styles.formRow}>
                <View style={styles.formGroup}>
                  <Text style={styles.label}>Category</Text>
                  <Pressable
                    onPress={() => setShowEditCategoryPicker(true)}
                    style={styles.pickerButton}
                  >
                    <Text style={styles.pickerButtonText}>
                      {selectedEditCategory?.name || 'Select Category'}
                    </Text>
                    <ChevronDown size={20} color="#4b5563" />
                  </Pressable>
                </View>

                <View style={styles.formGroup}>
                  <Text style={styles.label}>Priority</Text>
                  <Pressable
                    onPress={() => setShowEditPriorityPicker(true)}
                    style={styles.pickerButton}
                  >
                    <Text style={styles.pickerButtonText}>
                      {editTaskForm.priority.charAt(0).toUpperCase() + editTaskForm.priority.slice(1)}
                    </Text>
                    <ChevronDown size={20} color="#4b5563" />
                  </Pressable>
                </View>
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.label}>Due Date</Text>
                <TextInput
                  style={styles.input}
                  value={editTaskForm.due_date}
                  onChangeText={(value) => setEditTaskForm({ ...editTaskForm, due_date: value })}
                  placeholder="YYYY-MM-DD"
                  placeholderTextColor="#9ca3af"
                />
              </View>

              {/* Estimates Section - Only for user tasks */}
              {editingTask?.task_type === 'user_task' && (
                <View style={styles.estimatesSection}>
                  <Text style={styles.sectionTitle}>Estimates</Text>
                  
                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Estimated Time (minutes)</Text>
                    <TextInput
                      style={styles.input}
                      value={editTaskForm.time_minutes.toString()}
                      onChangeText={(value) => setEditTaskForm({ ...editTaskForm, time_minutes: parseInt(value) || 0 })}
                      keyboardType="numeric"
                      placeholderTextColor="#9ca3af"
                    />
                  </View>

                  <View style={styles.formRow}>
                    <View style={styles.formGroup}>
                      <Text style={styles.label}>Minimum Cost ($)</Text>
                      <TextInput
                        style={styles.input}
                        value={editTaskForm.cost_min.toString()}
                        onChangeText={(value) => setEditTaskForm({ ...editTaskForm, cost_min: parseFloat(value) || 0 })}
                        keyboardType="decimal-pad"
                        placeholderTextColor="#9ca3af"
                      />
                    </View>
                    <View style={styles.formGroup}>
                      <Text style={styles.label}>Maximum Cost ($)</Text>
                      <TextInput
                        style={styles.input}
                        value={editTaskForm.cost_max.toString()}
                        onChangeText={(value) => setEditTaskForm({ ...editTaskForm, cost_max: parseFloat(value) || 0 })}
                        keyboardType="decimal-pad"
                        placeholderTextColor="#9ca3af"
                      />
                    </View>
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Materials Needed</Text>
                    {editTaskForm.materials_needed.map((material, index) => (
                      <View key={index} style={styles.dynamicFieldRow}>
                        <TextInput
                          style={[styles.input, styles.dynamicFieldInput]}
                          value={material}
                          onChangeText={(value) => {
                            const updated = [...editTaskForm.materials_needed];
                            updated[index] = value;
                            setEditTaskForm({ ...editTaskForm, materials_needed: updated });
                          }}
                          placeholder="Enter material"
                          placeholderTextColor="#9ca3af"
                        />
                        {editTaskForm.materials_needed.length > 1 && (
                          <Pressable
                            onPress={() => {
                              const updated = editTaskForm.materials_needed.filter((_, i) => i !== index);
                              setEditTaskForm({ ...editTaskForm, materials_needed: updated });
                            }}
                            style={styles.removeButton}
                          >
                            <X size={16} color="#ef4444" />
                          </Pressable>
                        )}
                      </View>
                    ))}
                    <Pressable
                      onPress={() => setEditTaskForm({ 
                        ...editTaskForm, 
                        materials_needed: [...editTaskForm.materials_needed, ''] 
                      })}
                      style={styles.addFieldButton}
                    >
                      <Text style={styles.addFieldButtonText}>+ Add Material</Text>
                    </Pressable>
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Tools Needed</Text>
                    {editTaskForm.tools_needed.map((tool, index) => (
                      <View key={index} style={styles.dynamicFieldRow}>
                        <TextInput
                          style={[styles.input, styles.dynamicFieldInput]}
                          value={tool}
                          onChangeText={(value) => {
                            const updated = [...editTaskForm.tools_needed];
                            updated[index] = value;
                            setEditTaskForm({ ...editTaskForm, tools_needed: updated });
                          }}
                          placeholder="Enter tool"
                          placeholderTextColor="#9ca3af"
                        />
                        {editTaskForm.tools_needed.length > 1 && (
                          <Pressable
                            onPress={() => {
                              const updated = editTaskForm.tools_needed.filter((_, i) => i !== index);
                              setEditTaskForm({ ...editTaskForm, tools_needed: updated });
                            }}
                            style={styles.removeButton}
                          >
                            <X size={16} color="#ef4444" />
                          </Pressable>
                        )}
                      </View>
                    ))}
                    <Pressable
                      onPress={() => setEditTaskForm({ 
                        ...editTaskForm, 
                        tools_needed: [...editTaskForm.tools_needed, ''] 
                      })}
                      style={styles.addFieldButton}
                    >
                      <Text style={styles.addFieldButtonText}>+ Add Tool</Text>
                    </Pressable>
                  </View>
                </View>
              )}

              {error && (
                <Text style={styles.errorText}>{error}</Text>
              )}
            </WebScrollView>

            <View style={styles.modalFooter}>
              {editingTask?.task_type === 'user_task' ? (
                <Pressable
                  onPress={handleDeleteTask}
                  disabled={submitting}
                  style={[
                    styles.deleteButton,
                    deleteConfirmMode && styles.deleteButtonConfirm,
                    submitting && styles.deleteButtonDisabled
                  ]}
                >
                  <Text style={[
                    styles.deleteButtonText,
                    deleteConfirmMode && styles.deleteButtonTextConfirm
                  ]}>
                    {deleteConfirmMode ? 'Confirm' : 'Delete'}
                  </Text>
                </Pressable>
              ) : (
                <Text style={styles.cannotDeleteText}>Maintenance tasks cannot be deleted</Text>
              )}
              <View style={styles.modalFooterRight}>
                <Pressable
                  onPress={() => {
                    setIsTaskDetailOpen(false);
                    setDeleteConfirmMode(false);
                  }}
                  style={styles.cancelButton}
                >
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </Pressable>
                <Pressable
                  onPress={handleSaveTask}
                  disabled={submitting}
                  style={[styles.submitButton, submitting && styles.submitButtonDisabled]}
                >
                  {submitting ? (
                    <ActivityIndicator size="small" color="#ffffff" />
                  ) : (
                    <Text style={styles.submitButtonText}>Save</Text>
                  )}
                </Pressable>
              </View>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* Add Estimate Modal */}
      <Modal
        visible={isEstimateFormOpen}
        transparent
        animationType="slide"
        onRequestClose={() => setIsEstimateFormOpen(false)}
      >
        <View style={styles.modalOverlay}>
          <Pressable style={styles.modalBackdrop} onPress={() => setIsEstimateFormOpen(false)} />
          <View style={styles.estimateModalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Add Task Estimate</Text>
              <Pressable onPress={() => setIsEstimateFormOpen(false)}>
                <X size={24} color="#9ca3af" />
              </Pressable>
            </View>

            {selectedTask && (
              <View style={styles.selectedTaskInfo}>
                <Text style={styles.selectedTaskTitle}>{selectedTask.title}</Text>
                <Text style={styles.selectedTaskDescription}>{selectedTask.description}</Text>
              </View>
            )}

            <WebScrollView style={styles.modalBody} contentContainerStyle={styles.modalBodyContent}>
              <View style={styles.formGroup}>
                <Text style={styles.label}>Estimated Time (minutes)</Text>
                <TextInput
                  style={styles.input}
                  value={estimateForm.time_minutes.toString()}
                  onChangeText={(value) => setEstimateForm({ ...estimateForm, time_minutes: parseInt(value) || 0 })}
                  keyboardType="numeric"
                  placeholderTextColor="#9ca3af"
                />
              </View>

              <View style={styles.formRow}>
                <View style={styles.formGroup}>
                  <Text style={styles.label}>Minimum Cost ($)</Text>
                  <TextInput
                    style={styles.input}
                    value={estimateForm.cost_min.toString()}
                    onChangeText={(value) => setEstimateForm({ ...estimateForm, cost_min: parseFloat(value) || 0 })}
                    keyboardType="decimal-pad"
                    placeholderTextColor="#9ca3af"
                  />
                </View>
                <View style={styles.formGroup}>
                  <Text style={styles.label}>Maximum Cost ($)</Text>
                  <TextInput
                    style={styles.input}
                    value={estimateForm.cost_max.toString()}
                    onChangeText={(value) => setEstimateForm({ ...estimateForm, cost_max: parseFloat(value) || 0 })}
                    keyboardType="decimal-pad"
                    placeholderTextColor="#9ca3af"
                  />
                </View>
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.label}>Materials Needed</Text>
                {estimateForm.materials_needed.map((material, index) => (
                  <View key={index} style={styles.dynamicFieldRow}>
                    <TextInput
                      style={[styles.input, styles.dynamicFieldInput]}
                      value={material}
                      onChangeText={(value) => updateMaterialField(index, value)}
                      placeholder="Enter material"
                      placeholderTextColor="#9ca3af"
                    />
                  </View>
                ))}
                <Pressable
                  onPress={addMaterialField}
                  style={styles.addFieldButton}
                >
                  <Text style={styles.addFieldButtonText}>+ Add Another Material</Text>
                </Pressable>
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.label}>Tools Needed</Text>
                {estimateForm.tools_needed.map((tool, index) => (
                  <View key={index} style={styles.dynamicFieldRow}>
                    <TextInput
                      style={[styles.input, styles.dynamicFieldInput]}
                      value={tool}
                      onChangeText={(value) => updateToolField(index, value)}
                      placeholder="Enter tool"
                      placeholderTextColor="#9ca3af"
                    />
                  </View>
                ))}
                <Pressable
                  onPress={addToolField}
                  style={styles.addFieldButton}
                >
                  <Text style={styles.addFieldButtonText}>+ Add Another Tool</Text>
                </Pressable>
              </View>

              {error && (
                <Text style={styles.errorText}>{error}</Text>
              )}
            </WebScrollView>

            <View style={styles.modalFooter}>
              <Pressable
                onPress={() => setIsEstimateFormOpen(false)}
                style={styles.cancelButton}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </Pressable>
              <Pressable
                onPress={handleEstimateSubmit}
                disabled={submitting}
                style={[styles.submitButton, submitting && styles.submitButtonDisabled]}
              >
                {submitting ? (
                  <ActivityIndicator size="small" color="#ffffff" />
                ) : (
                  <Text style={styles.submitButtonText}>Save Estimate</Text>
                )}
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      {/* Dropdown Pickers */}
      <DropdownPicker
        visible={showStatusFilterPicker}
        onClose={() => setShowStatusFilterPicker(false)}
        options={[
          { value: 'all', label: 'All Statuses' },
          { value: 'pending', label: 'Pending' },
          { value: 'in_progress', label: 'In Progress' },
          { value: 'completed', label: 'Completed' },
          { value: 'cancelled', label: 'Cancelled' }
        ]}
        selectedValue={statusFilter}
        onSelect={setStatusFilter}
      />

      <DropdownPicker
        visible={showSkillFilterPicker}
        onClose={() => setShowSkillFilterPicker(false)}
        options={[
          { value: 'all', label: 'All Skill Levels' },
          { value: 'diy', label: 'DIY' },
          { value: 'handyman', label: 'Handyman' },
          { value: 'professional', label: 'Professional' }
        ]}
        selectedValue={skillFilter}
        onSelect={setSkillFilter}
      />

      <DropdownPicker
        visible={showTypeFilterPicker}
        onClose={() => setShowTypeFilterPicker(false)}
        options={[
          { value: 'all', label: 'All Task Types' },
          { value: 'user_task', label: 'Custom Tasks' },
          { value: 'maintenance_task', label: 'Maintenance Tasks' }
        ]}
        selectedValue={typeFilter}
        onSelect={setTypeFilter}
      />

      <DropdownPicker
        visible={showCategoryPicker}
        onClose={() => setShowCategoryPicker(false)}
        options={categories.map(cat => ({ value: cat.id, label: cat.name }))}
        selectedValue={formData.category_id}
        onSelect={(value) => setFormData({ ...formData, category_id: value })}
      />

      <DropdownPicker
        visible={showPriorityPicker}
        onClose={() => setShowPriorityPicker(false)}
        options={[
          { value: 'low', label: 'Low' },
          { value: 'normal', label: 'Normal' },
          { value: 'high', label: 'High' },
          { value: 'urgent', label: 'Urgent' }
        ]}
        selectedValue={formData.priority}
        onSelect={(value) => setFormData({ ...formData, priority: value as Task['priority'] })}
      />

      <DropdownPicker
        visible={showStatusPicker}
        onClose={() => {
          setShowStatusPicker(false);
          setStatusPickerTask(null);
        }}
        options={[
          { value: 'pending', label: 'Pending' },
          { value: 'in_progress', label: 'In Progress' },
          { value: 'completed', label: 'Completed' },
          { value: 'cancelled', label: 'Cancelled' }
        ]}
        selectedValue={statusPickerTask?.status || 'pending'}
        onSelect={(value) => {
          if (statusPickerTask) {
            handleStatusChange(statusPickerTask, value as Task['status']);
          }
          setShowStatusPicker(false);
          setStatusPickerTask(null);
        }}
      />

      <DropdownPicker
        visible={showEditCategoryPicker}
        onClose={() => setShowEditCategoryPicker(false)}
        options={categories.map(cat => ({ value: cat.id, label: cat.name }))}
        selectedValue={editTaskForm.category_id}
        onSelect={(value) => setEditTaskForm({ ...editTaskForm, category_id: value })}
      />

      <DropdownPicker
        visible={showEditPriorityPicker}
        onClose={() => setShowEditPriorityPicker(false)}
        options={[
          { value: 'low', label: 'Low' },
          { value: 'normal', label: 'Normal' },
          { value: 'high', label: 'High' },
          { value: 'urgent', label: 'Urgent' }
        ]}
        selectedValue={editTaskForm.priority}
        onSelect={(value) => setEditTaskForm({ ...editTaskForm, priority: value as Task['priority'] })}
      />
      </WebScrollView>

      {/* Completion Modal */}
      {showCompleteModal && taskToComplete && (
        <Modal
          visible={showCompleteModal}
          transparent
          animationType="slide"
          onRequestClose={handleCloseCompleteModal}
        >
          <View style={styles.modalOverlay}>
            <Pressable style={styles.modalBackdrop} onPress={handleCloseCompleteModal} />
            <View style={styles.completeModalContent}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Mark Task Complete</Text>
                <Pressable onPress={handleCloseCompleteModal}>
                  <X size={20} color="#9ca3af" />
                </Pressable>
              </View>

              <WebScrollView style={styles.modalBody}>
                <View style={styles.completeFormContainer}>
                  <Text style={styles.completeTaskName}>{taskToComplete.title}</Text>
                  <Text style={styles.completeTaskDescription}>
                    {taskToComplete.description}
                  </Text>

                  {/* Completion Date */}
                  <View style={styles.formGroup}>
                    <Text style={styles.formLabel}>When was this completed?</Text>
                    {Platform.OS === 'web' ? (
                      <input
                        type="date"
                        value={completionData.completion_date}
                        onChange={(e) => setCompletionData({ ...completionData, completion_date: e.target.value })}
                        style={{
                          padding: '12px',
                          borderWidth: '1px',
                          borderStyle: 'solid',
                          borderColor: '#d1d5db',
                          borderRadius: '8px',
                          fontSize: '14px',
                          color: '#111827',
                          backgroundColor: '#ffffff',
                          width: '100%',
                          fontFamily: 'inherit',
                        }}
                      />
                    ) : (
                      <TextInput
                        style={styles.dateInput}
                        value={completionData.completion_date}
                        onChangeText={(text) => setCompletionData({ ...completionData, completion_date: text })}
                        placeholder="YYYY-MM-DD"
                        placeholderTextColor="#9ca3af"
                      />
                    )}
                  </View>

                  {/* Who performed */}
                  <View style={styles.formGroup}>
                    <Text style={styles.formLabel}>Who did this work?</Text>
                    <View style={styles.radioGroup}>
                      <Pressable
                        onPress={() => setCompletionData({ ...completionData, performed_by: 'homeowner' })}
                        style={[
                          styles.radioOption,
                          completionData.performed_by === 'homeowner' && styles.radioOptionSelected
                        ]}
                      >
                        <User size={20} color={completionData.performed_by === 'homeowner' ? '#ffffff' : '#6b7280'} />
                        <Text style={[
                          styles.radioText,
                          completionData.performed_by === 'homeowner' && styles.radioTextSelected
                        ]}>
                          I did it myself
                        </Text>
                      </Pressable>
                      <Pressable
                        onPress={() => setCompletionData({ ...completionData, performed_by: 'professional' })}
                        style={[
                          styles.radioOption,
                          completionData.performed_by === 'professional' && styles.radioOptionSelected
                        ]}
                      >
                        <Briefcase size={20} color={completionData.performed_by === 'professional' ? '#ffffff' : '#6b7280'} />
                        <Text style={[
                          styles.radioText,
                          completionData.performed_by === 'professional' && styles.radioTextSelected
                        ]}>
                          Professional service
                        </Text>
                      </Pressable>
                    </View>
                  </View>

                  {/* Notes */}
                  <View style={styles.formGroup}>
                    <Text style={styles.formLabel}>Notes (optional)</Text>
                    <TextInput
                      style={styles.notesInput}
                      value={completionData.notes}
                      onChangeText={(text) => setCompletionData({ ...completionData, notes: text })}
                      placeholder="Add any notes about this maintenance..."
                      placeholderTextColor="#9ca3af"
                      multiline
                      numberOfLines={4}
                      textAlignVertical="top"
                    />
                  </View>
                </View>
              </WebScrollView>

              <View style={styles.modalFooter}>
                <Pressable
                  onPress={handleCloseCompleteModal}
                  style={styles.cancelButton}
                >
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </Pressable>
                <Pressable
                  onPress={handleCompleteTask}
                  disabled={completing}
                  style={[styles.submitButton, completing && styles.submitButtonDisabled]}
                >
                  {completing ? (
                    <>
                      <ActivityIndicator size="small" color="#ffffff" />
                      <Text style={styles.submitButtonText}>Saving...</Text>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={16} color="#ffffff" />
                      <Text style={styles.submitButtonText}>Mark Complete</Text>
                    </>
                  )}
                </Pressable>
              </View>
            </View>
          </View>
        </Modal>
      )}

      {/* Quick Add Task bottom sheet — Google-Tasks-style minimal input.
          Submitting saves the task immediately, clears the input, and
          re-focuses so the user can keep adding tasks rapidly. AI
          enrichment runs in the background after each save. */}
      <Modal
        visible={showQuickAddSheet}
        transparent
        animationType="slide"
        onRequestClose={handleCloseQuickAddSheet}
      >
        <KeyboardAvoidingView
          style={styles.modalOverlay}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          <Pressable
            style={styles.modalBackdrop}
            onPress={handleCloseQuickAddSheet}
          />
          <View style={styles.quickAddSheet}>
            <View style={styles.quickAddInputRow}>
              <TextInput
                ref={quickAddInputRef}
                style={styles.quickAddInput}
                placeholder="Add a task"
                placeholderTextColor="#9ca3af"
                value={quickAddTitle}
                onChangeText={(text) => {
                  setQuickAddTitle(text);
                  if (quickAddError) setQuickAddError(null);
                }}
                onSubmitEditing={handleQuickAddSubmit}
                returnKeyType="done"
                blurOnSubmit={false}
                editable={!quickAddSubmitting}
                autoFocus
                {...(Platform.OS === 'web'
                  ? ({ onKeyPress: (e: any) => {
                      if (e?.nativeEvent?.key === 'Escape') {
                        handleCloseQuickAddSheet();
                      }
                    } } as any)
                  : {})}
              />
            </View>

            {quickAddError && (
              <Text style={[styles.errorText, styles.quickAddError]}>
                {quickAddError}
              </Text>
            )}

            <View style={styles.quickAddActionRow}>
              <Pressable
                onPress={handleCloseQuickAddSheet}
                style={styles.quickAddIconButton}
                disabled={quickAddSubmitting}
                accessibilityLabel="Close"
              >
                <X size={20} color="#6b7280" />
              </Pressable>

              <View style={styles.quickAddHelpTextWrap}>
                <Sparkles size={14} color="#9ca3af" />
                <Text style={styles.quickAddHelpText}>
                  Just type — we'll fill in the rest.
                </Text>
              </View>

              <Pressable
                onPress={handleQuickAddSubmit}
                disabled={quickAddSubmitting || !quickAddTitle.trim()}
                style={[
                  styles.quickAddSaveButton,
                  (quickAddSubmitting || !quickAddTitle.trim()) &&
                    styles.quickAddSaveButtonDisabled,
                ]}
              >
                {quickAddSubmitting ? (
                  <ActivityIndicator size="small" color="#1d4ed8" />
                ) : (
                  <Text style={styles.quickAddSaveButtonText}>Save</Text>
                )}
              </Pressable>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* Service Request Creation Modal */}
      <Modal
        visible={showServiceRequestModal}
        transparent
        animationType="slide"
        onRequestClose={() => !creatingServiceRequest && handleCloseServiceRequestModal()}
      >
        <View style={styles.modalOverlay}>
          <Pressable 
            style={styles.modalBackdrop} 
            onPress={() => !creatingServiceRequest && handleCloseServiceRequestModal()} 
          />
          <View style={styles.serviceRequestModalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {serviceRequestSuccess ? 'Service Request Created!' : 'Request Service'}
              </Text>
              {!creatingServiceRequest && !serviceRequestSuccess && (
                <Pressable onPress={handleCloseServiceRequestModal}>
                  <X size={20} color="#6b7280" />
                </Pressable>
              )}
            </View>

            <WebScrollView style={styles.modalBody} contentContainerStyle={styles.modalBodyContent}>
              {serviceRequestSuccess ? (
                <View style={styles.successMessageContainer}>
                  <View style={styles.successIconContainer}>
                    <CheckCircle2 size={48} color="#16a34a" />
                  </View>
                  <Text style={styles.successMessageTitle}>
                    Service request created successfully!
                  </Text>
                  <Text style={styles.successMessageText}>
                    Your service request has been submitted. You can track its progress in the Service Requests section.
                  </Text>
                </View>
              ) : (
                <>
                  <Text style={styles.serviceRequestDescription}>
                    This will create a service request for professional help with this task.
                  </Text>
                  
                  {taskForServiceRequest && (
                    <View style={styles.taskPreviewContainer}>
                      <View style={styles.taskPreviewHeader}>
                        <Text style={styles.taskPreviewLabel}>Task Details:</Text>
                      </View>
                      <View style={styles.taskPreviewContent}>
                        <Text style={styles.taskPreviewTitle}>{taskForServiceRequest.title}</Text>
                        <Text style={styles.taskPreviewDescription}>{taskForServiceRequest.description}</Text>
                        <View style={styles.taskPreviewMeta}>
                          <View style={[styles.badge, getPriorityColor(taskForServiceRequest.priority)]}>
                            <Text style={[styles.badgeText, getPriorityTextColor(taskForServiceRequest.priority)]}>
                              {taskForServiceRequest.priority.charAt(0).toUpperCase() + taskForServiceRequest.priority.slice(1)} Priority
                            </Text>
                          </View>
                          <View style={[styles.metaBadge, getSkillLevelColor(taskForServiceRequest.skill_level)]}>
                            {getSkillLevelIcon(taskForServiceRequest.skill_level)}
                            <Text style={styles.metaBadgeText}>{getSkillLevelLabel(taskForServiceRequest.skill_level)}</Text>
                          </View>
                        </View>
                      </View>
                    </View>
                  )}

                  <View style={styles.serviceRequestInfoBox}>
                    <Text style={styles.serviceRequestInfoText}>
                      A service request will be created with the same title, description, and priority. Our team will review it and get back to you shortly.
                    </Text>
                  </View>
                </>
              )}
            </WebScrollView>

            {!serviceRequestSuccess && (
              <View style={styles.modalFooter}>
                <Pressable
                  onPress={handleCloseServiceRequestModal}
                  style={styles.cancelButton}
                  disabled={creatingServiceRequest}
                >
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </Pressable>
                
                <Pressable
                  onPress={handleCreateServiceRequest}
                  style={[
                    styles.submitButton,
                    creatingServiceRequest && styles.submitButtonDisabled
                  ]}
                  disabled={creatingServiceRequest}
                >
                  {creatingServiceRequest ? (
                    <ActivityIndicator size="small" color="#ffffff" />
                  ) : (
                    <Text style={styles.submitButtonText}>Create Service Request</Text>
                  )}
                </Pressable>
              </View>
            )}
          </View>
        </View>
      </Modal>

      {/* Fixed Add Task Button */}
      <View style={styles.fixedButtonContainer}>
        <Pressable
          onPress={handleAddTaskClick}
          style={styles.addButton}
        >
          <Sparkles size={20} color="#ffffff" />
          <Text style={styles.addButtonText}>Add Task</Text>
        </Pressable>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    padding: 0,
    // Clear fixed Add Task bar: bottom offset + container padding + button
    // (~16 + 12 + ~52 + 12 + safe margin). Web keeps a larger pad because
    // the web navbar is also position: fixed at the bottom of the viewport.
    paddingBottom: Platform.OS === 'web' ? 128 : 96,
    gap: 0,
  },
  header: {
    gap: 4,
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 4,
  },
  headerContent: {
    gap: 2,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: '#111827',
  },
  subtitle: {
    fontSize: 13,
    color: '#4b5563',
  },
  fixedButtonContainer: {
    position: 'absolute',
    // On native, mainContainer (App.tsx) is a flex column with the page
    // (mainContent) above and the 64px Navbar below as a sibling. Anything
    // absolute-positioned inside the page is clipped to the page's box,
    // so we need a *positive* bottom value to keep the button above the
    // page's bottom edge — i.e. above the navbar.
    bottom: Platform.OS === 'web' ? 32 : 8,
    left: 0,
    right: 0,
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#ffffff',
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    ...(Platform.OS === 'web' && {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: -2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
    }),
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 20,
    paddingVertical: 14,
    backgroundColor: '#000000',
    borderRadius: 8,
    width: '100%',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  addButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 0,
    borderWidth: 0,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#e5e7eb',
    marginTop: 16,
  },
  cardContent: {
    padding: 0,
  },
  homePickerSection: {
    paddingHorizontal: 16,
    paddingTop: 4,
    paddingBottom: 12,
  },
  filtersSection: {
    gap: 12,
    marginBottom: 16,
  },
  filterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  pickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    backgroundColor: '#ffffff',
    minWidth: 200,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  pickerButtonText: {
    fontSize: 14,
    color: '#111827',
  },
  filterToggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  filterToggleText: {
    fontSize: 14,
    color: '#4b5563',
  },
  activeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: '#dbeafe',
    borderRadius: 6,
  },
  activeBadgeText: {
    fontSize: 12,
    color: '#1e40af',
  },
  refreshButton: {
    padding: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  filtersContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    backgroundColor: '#f9fafb',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  filterPickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    backgroundColor: '#ffffff',
    minWidth: 150,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  filterPickerText: {
    fontSize: 14,
    color: '#111827',
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
    gap: 16,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '500',
    color: '#111827',
  },
  emptyText: {
    fontSize: 14,
    color: '#4b5563',
    textAlign: 'center',
    paddingHorizontal: 32,
  },
  emptyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#000000',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  emptyButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
  },
  draggableContainer: {
    flex: 1,
  },
  tasksList: {
    gap: 0,
  },
  noActiveTasksHint: {
    paddingVertical: 12,
    alignItems: 'center',
  },
  noActiveTasksHintText: {
    fontSize: 14,
    color: '#9ca3af',
  },
  completedSection: {
    paddingTop: 4,
  },
  completedSectionAfterActive: {
    marginTop: 8,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  completedSectionHeading: {
    fontSize: 13,
    fontWeight: '600',
    color: '#6b7280',
    letterSpacing: 0.3,
    textTransform: 'uppercase' as const,
    marginBottom: 4,
    paddingHorizontal: 4,
  },
  completedTasksList: {
    gap: 0,
  },
  taskCard: {
    backgroundColor: '#ffffff',
    borderRadius: 0,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    padding: 16,
    paddingHorizontal: 8,
    marginBottom: 0,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  taskCardJustAdded: {
    backgroundColor: '#fffbeb',
    borderLeftWidth: 3,
    borderLeftColor: '#f59e0b',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 0 0 1px rgba(245, 158, 11, 0.35)',
      transition: 'background-color 0.4s ease, box-shadow 0.4s ease',
    }),
  },
  taskCardExpanded: {
    backgroundColor: '#f9fafb',
  },
  taskCardDragging: {
    backgroundColor: '#f3f4f6',
    ...(Platform.OS === 'web' && {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.15,
      shadowRadius: 8,
      elevation: 8,
    }),
  },
  taskCardDragOver: {
    borderLeftWidth: 3,
    borderLeftColor: '#2563eb',
    backgroundColor: '#eff6ff',
  },
  taskCardContent: {
    gap: 8,
    position: 'relative',
  },
  dragHandleContainer: {
    position: 'absolute',
    // Center the handle vertically on the collapsed-row baseline so it
    // visually anchors to the title rather than floating in the corner.
    top: -2,
    left: -4,
    zIndex: 10,
    ...(Platform.OS === 'web' && {
      cursor: 'grab',
    }),
  },
  dragHandle: {
    // Hit target sized to Apple's 44pt minimum; keep the visual icon small
    // so the row still feels tidy. Padding gives the touch area without
    // changing the icon footprint.
    width: 36,
    height: 44,
    paddingHorizontal: 6,
    paddingVertical: 13,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'grab',
      transition: 'background-color 150ms ease, opacity 150ms ease',
    }),
  },
  dragHandleHovered: {
    backgroundColor: '#f3f4f6',
  },
  dragHandlePressed: {
    backgroundColor: '#e5e7eb',
    ...(Platform.OS === 'web' && {
      cursor: 'grabbing',
    }),
  },
  taskHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    paddingLeft: 36, // Make room for drag handle on left + horizontal padding
    paddingRight: 28, // Make room for chevron on right
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  taskHeaderPressed: {
    opacity: 0.92,
  },
  taskHeaderContent: {
    flex: 1,
    gap: 8,
  },
  chevronContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 4,
  },
  compactActionBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingTop: 8,
    paddingLeft: 36, // Match taskHeader padding
    paddingRight: 28, // Match taskHeader padding
  },
  compactActionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  compactActionButtonPrimary: {
    backgroundColor: '#000000',
    borderColor: '#000000',
  },
  compactActionButtonDisabled: {
    backgroundColor: '#f9fafb',
    borderColor: '#e5e7eb',
  },
  compactActionButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#374151',
  },
  compactActionButtonTextPrimary: {
    color: '#ffffff',
  },
  compactActionButtonTextDisabled: {
    color: '#9ca3af',
  },
  taskTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
  },
  taskBadges: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  badgeText: {
    fontSize: 12,
    fontWeight: '500',
  },
  priorityUrgent: {
    backgroundColor: '#fee2e2',
  },
  priorityUrgentText: {
    color: '#dc2626',
  },
  priorityHigh: {
    backgroundColor: '#fed7aa',
  },
  priorityHighText: {
    color: '#ea580c',
  },
  priorityNormal: {
    backgroundColor: '#dbeafe',
  },
  priorityNormalText: {
    color: '#2563eb',
  },
  priorityLow: {
    backgroundColor: '#dcfce7',
  },
  priorityLowText: {
    color: '#16a34a',
  },
  priorityDefault: {
    backgroundColor: '#f3f4f6',
  },
  priorityDefaultText: {
    color: '#4b5563',
  },
  statusCompleted: {
    backgroundColor: '#dcfce7',
  },
  statusCompletedText: {
    color: '#16a34a',
  },
  statusInProgress: {
    backgroundColor: '#dbeafe',
  },
  statusInProgressText: {
    color: '#2563eb',
  },
  statusCancelled: {
    backgroundColor: '#f3f4f6',
  },
  statusCancelledText: {
    color: '#4b5563',
  },
  statusPending: {
    backgroundColor: '#fed7aa',
  },
  statusPendingText: {
    color: '#ea580c',
  },
  dueDateText: {
    fontSize: 12,
    color: '#6b7280',
  },
  taskExpandedContent: {
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    marginTop: 8,
    gap: 12,
  },
  taskDescription: {
    fontSize: 14,
    color: '#4b5563',
  },
  taskMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
  },
  metaBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  metaBadgeText: {
    fontSize: 12,
    color: '#111827',
  },
  skillDiy: {
    backgroundColor: '#dcfce7',
  },
  skillHandyman: {
    backgroundColor: '#dbeafe',
  },
  skillProfessional: {
    backgroundColor: '#f3e8ff',
  },
  skillDefault: {
    backgroundColor: '#f3f4f6',
  },
  taskTypeUser: {
    backgroundColor: '#eef2ff',
  },
  taskTypeMaintenance: {
    backgroundColor: '#fef3c7',
  },
  estimateDetails: {
    gap: 16,
  },
  estimateSection: {
    gap: 8,
  },
  estimateSectionTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  estimateItem: {
    fontSize: 12,
    color: '#4b5563',
    marginLeft: 8,
  },
  taskActions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  statusSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  statusLabel: {
    fontSize: 14,
    color: '#374151',
  },
  statusPickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  statusPickerText: {
    fontSize: 12,
    color: '#111827',
  },
  actionButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  secondaryButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  secondaryButtonText: {
    fontSize: 12,
    color: '#111827',
  },
  serviceRequestButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: '#2563eb',
    borderRadius: 8,
    backgroundColor: '#eff6ff',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  serviceRequestButtonText: {
    fontSize: 12,
    color: '#2563eb',
    fontWeight: '500',
  },
  primaryButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#000000',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  primaryButtonText: {
    fontSize: 12,
    color: '#ffffff',
    fontWeight: '500',
  },
  modalOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalBackdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    // Native sheets get an explicit pixel height applied inline at render
    // time (see useWindowDimensions usage in the modal markup). Without an
    // explicit height, the inner ScrollView (modalBody) with flex: 1 has no
    // bounded parent and collapses to 0 height on iOS — that's why the
    // form inputs were invisible. Web keeps the percentage-based cap since
    // browser flexbox handles flex/maxHeight more permissively.
    ...(Platform.OS === 'web'
      ? { maxHeight: '90%', maxWidth: 768, width: '100%', marginHorizontal: 'auto' }
      : {}),
  },
  estimateModalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    maxHeight: '90%',
    margin: 16,
    ...(Platform.OS === 'web' && {
      maxWidth: 512,
      width: '100%',
      marginHorizontal: 'auto',
    }),
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  modalBody: {
    flex: 1,
  },
  modalBodyContent: {
    padding: 24,
    gap: 24,
  },
  formGroup: {
    flex: 1,
    gap: 8,
  },
  formRow: {
    flexDirection: 'row',
    gap: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  input: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    fontSize: 16,
    color: '#111827',
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none' as any,
    }),
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  dynamicFieldRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  dynamicFieldInput: {
    flex: 1,
  },
  removeButton: {
    padding: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  addFieldButton: {
    paddingVertical: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  addFieldButtonText: {
    fontSize: 14,
    color: '#2563eb',
    fontWeight: '500',
  },
  estimatesSection: {
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    gap: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '500',
    color: '#111827',
  },
  selectedTaskInfo: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  selectedTaskTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
    marginBottom: 4,
  },
  selectedTaskDescription: {
    fontSize: 14,
    color: '#4b5563',
  },
  errorText: {
    fontSize: 14,
    color: '#ef4444',
  },
  modalFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 24,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  modalFooterRight: {
    flexDirection: 'row',
    gap: 12,
  },
  cancelButton: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  cancelButtonText: {
    fontSize: 16,
    color: '#4b5563',
  },
  submitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#000000',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  submitButtonDisabled: {
    opacity: 0.5,
  },
  submitButtonText: {
    fontSize: 16,
    color: '#ffffff',
    fontWeight: '500',
  },
  deleteButton: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: '#fca5a5',
    borderRadius: 8,
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  deleteButtonConfirm: {
    backgroundColor: '#dc2626',
    borderColor: '#dc2626',
  },
  deleteButtonDisabled: {
    opacity: 0.5,
  },
  deleteButtonText: {
    fontSize: 16,
    color: '#dc2626',
    fontWeight: '500',
  },
  deleteButtonTextConfirm: {
    color: '#ffffff',
  },
  cannotDeleteText: {
    fontSize: 14,
    color: '#6b7280',
    paddingVertical: 12,
  },
  pickerModalOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  pickerModalBackdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  pickerModalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '50%',
  },
  pickerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  pickerHeaderText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  pickerOptions: {
    maxHeight: 300,
  },
  pickerOption: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  pickerOptionSelected: {
    backgroundColor: '#f3f4f6',
  },
  pickerOptionText: {
    fontSize: 16,
    color: '#111827',
  },
  pickerOptionTextSelected: {
    fontWeight: '500',
    color: '#000000',
  },
  // Quick Add Task bottom sheet — Google-Tasks-style minimal sheet that
  // hugs the keyboard. We deliberately keep it short so the focus stays
  // on a single text field; KeyboardAvoidingView lifts it above the
  // keyboard on iOS, and the inline action row sits flush at the bottom.
  quickAddSheet: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingTop: 20,
    paddingHorizontal: 20,
    paddingBottom: 12,
    gap: 12,
    ...(Platform.OS === 'web' && {
      maxWidth: 600,
      width: '100%',
      marginHorizontal: 'auto',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: -4 },
      shadowOpacity: 0.08,
      shadowRadius: 12,
    }),
  },
  quickAddInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  quickAddInput: {
    flex: 1,
    fontSize: 16,
    color: '#111827',
    paddingVertical: 8,
    paddingHorizontal: 0,
    backgroundColor: 'transparent',
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none' as any,
      border: 'none' as any,
    }),
  },
  quickAddError: {
    paddingHorizontal: 0,
  },
  quickAddActionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6',
  },
  quickAddIconButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  quickAddHelpTextWrap: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 4,
  },
  quickAddHelpText: {
    fontSize: 12,
    color: '#9ca3af',
    flexShrink: 1,
  },
  quickAddSaveButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    minWidth: 64,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  quickAddSaveButtonDisabled: {
    opacity: 0.4,
  },
  quickAddSaveButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1d4ed8',
  },
  // Inline spinner shown next to a task title while the AI fills in
  // its details. Kept small and subtle so the row doesn't visually
  // shift around once the spinner disappears.
  enrichingIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
  },
  enrichingText: {
    fontSize: 12,
    color: '#6b7280',
    fontStyle: 'italic',
  },
  // Service Request Modal styles
  serviceRequestModalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '80%',
    ...(Platform.OS === 'web' && {
      maxWidth: 600,
      width: '100%',
      marginHorizontal: 'auto',
    }),
  },
  serviceRequestDescription: {
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 20,
    marginBottom: 16,
  },
  taskPreviewContainer: {
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    overflow: 'hidden',
    marginBottom: 16,
  },
  taskPreviewHeader: {
    backgroundColor: '#f3f4f6',
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  taskPreviewLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  taskPreviewContent: {
    padding: 16,
    gap: 12,
  },
  taskPreviewTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  taskPreviewDescription: {
    fontSize: 14,
    color: '#4b5563',
    lineHeight: 20,
  },
  taskPreviewMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4,
  },
  serviceRequestInfoBox: {
    backgroundColor: '#eff6ff',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#bfdbfe',
  },
  serviceRequestInfoText: {
    fontSize: 13,
    color: '#1e40af',
    lineHeight: 18,
  },
  successMessageContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 32,
    gap: 16,
  },
  successIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#dcfce7',
    alignItems: 'center',
    justifyContent: 'center',
  },
  successMessageTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    textAlign: 'center',
  },
  successMessageText: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
    lineHeight: 20,
    paddingHorizontal: 32,
  },
  completeModalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    maxWidth: 500,
    width: '100%',
    maxHeight: '80%',
    ...Platform.select({
      web: {
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
      },
      default: {
        ...shadows.large,
      },
    }),
  },
  completeFormContainer: {
    gap: 20,
  },
  completeTaskName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  completeTaskDescription: {
    fontSize: 14,
    color: '#6b7280',
    marginBottom: 8,
  },
  formGroup: {
    gap: 8,
  },
  formLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  dateInput: {
    padding: 12,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    fontSize: 14,
    color: '#111827',
    backgroundColor: '#ffffff',
  },
  radioGroup: {
    gap: 12,
  },
  radioOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  radioOptionSelected: {
    backgroundColor: '#000000',
    borderColor: '#000000',
  },
  radioText: {
    fontSize: 14,
    color: '#111827',
  },
  radioTextSelected: {
    color: '#ffffff',
  },
  notesInput: {
    padding: 12,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    fontSize: 14,
    color: '#111827',
    backgroundColor: '#ffffff',
    minHeight: 100,
  },
  cancelButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  cancelButtonText: {
    fontSize: 14,
    color: '#6b7280',
  },
  submitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: '#000000',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  submitButtonDisabled: {
    opacity: 0.5,
    ...(Platform.OS === 'web' && {
      cursor: 'not-allowed',
    }),
  },
  submitButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#ffffff',
  },
});

export default HoneyDoList;
